import { zodResolver } from '@hookform/resolvers/zod'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs from 'dayjs'
// import truckLogo from 'assets/Icon-OpticTruckWorks-02.png'
import InfoIcon from '@mui/icons-material/Info'
import ReplayIcon from '@mui/icons-material/Replay'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import PhotoCamera from '@mui/icons-material/PhotoCamera'
import OpenInNew from '@mui/icons-material/OpenInNew'
import CheckCircle from '@mui/icons-material/CheckCircle'
import { alpha } from '@mui/material/styles'
import { Vehicle, TitleStatus } from '@otw/models/inventory'
import {
  AdditionalExpenseItem,
  AppliedEstimatedRepairAddon,
  PickupLocation,
  GatePassFile,
} from '@otw/models'
import { opticTruckBlue as truckLogo } from 'assets'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { usePermissions } from '@/hooks/auth/usePermissions'
import { fetchVinData } from 'core/inventory/fetchVinData'
import { inventoryDuplicateMessage, nextCarriedAddVin } from 'core/inventory/inventoryUniqueness'
import {
  getMaxVehicleYear,
  getVehicleModelOptions,
  getVehicleSizeOptions,
  makeOptions,
  vehicleFuelOptions,
  vehicleStatusColorMap,
  vehicleStatusOptions,
  vehicleTypeOptions,
} from 'constants/inventory'
import { getDealQualityInfo, useCalculateDeal, getPriceForQuality } from 'hooks/graybook/useCalculateDeal'
import { useInventory } from 'hooks/inventory/useInventory'
import { buildUnmarkReadyForPickupUpdate } from '@/components/inventory/utils/vehicleFinancialToggleUpdates'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import { useAuctions } from 'hooks/inventory/useAuctions'
import { useExpenseSettings } from 'hooks/admin/useExpenseSettings'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { SAFE_AREA_BOTTOM, SAFE_AREA_TOP, TOUCH_MIN } from 'core/theme.tokens'
import { isGvwrOver10k } from 'hooks/useVinDecoder'
import React, { lazy, Suspense, useEffect, useRef, useState, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import states from 'states-us'
import { z } from 'zod'
import FieldResetConfirmationDialog from '../dialogs/FieldResetConfirmationDialog'
import { RepairAddonQuantityStepper } from '@/components/RepairAddonQuantityStepper'
import {
  estimatedCategoryLabel,
  repairAddonLineTotal,
  toggleRepairAddonOnFields,
  updateRepairAddonQuantityOnFields,
} from '@/core/expenses/estimatedRepairAddons'
import { useVehicleFormSession } from 'hooks/inventory/useVehicleFormSession'
import { usePhotosForInventoryObject } from 'core/inventory/getPhotosForInventoryObject'
import { renderReadOnlyField } from './ReadOnlyFields'
import { AttachFile, Download, Add, Save, Edit, Delete } from '@mui/icons-material'
import { soldDialogShippingFromVehicle, storedShippingFromDialog } from '@/components/inventory/dialogs/soldShipping'

const LazySoldDialog = lazy(() => import('@/components/inventory/dialogs/SoldDialog'))
import { LinearProgress, Divider, Card, CardContent } from '@mui/material'
import { formatFileSize } from '@/utils/formatFileSize'
import { Timestamp } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from '@firebase/storage'
import { storage, db } from '@/config/firebase'
import { useQueryClient } from '@tanstack/react-query'
import {
  AddLocationDialog,
  AuctionDialog,
} from 'views/SuperAdmin/Management/PickupLocations/components'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import {
  filterPickupLocationOptions,
  formatPickupLocationAddress,
  getPickupLocationContactLabel,
  getPickupLocationSearchLabel,
} from 'views/SuperAdmin/Management/LeadManagement/util/pickupLocationDisplay'


// Calculate price needed for a specific deal quality

// Map VIN decoder make values to our allowed make values
const mapVinMakeToAllowedMake = (vinMake: string | undefined | null): string => {
  if (!vinMake) return ''

  // Normalize the VIN make value (lowercase and trim)
  const normalizedVinMake = vinMake.toLowerCase().trim()

  // Map common VIN decoder values to our allowed values
  const makeMapping: Record<string, string> = {
    ford: 'Ford',
    chevrolet: 'Chevrolet',
    chevy: 'Chevrolet',
    gmc: 'GMC',
    freightliner: 'Freightliner',
    'general motors': 'GMC',
    gm: 'GMC',
    mercedes: 'Mercedes-Benz',
    'mercedes benz': 'Mercedes-Benz',
    'mercedes-benz': 'Mercedes-Benz',
    isuzu: 'Isuzu',
  }

  // Return the mapped value if it exists, otherwise empty string
  return makeMapping[normalizedVinMake] || ''
}

// Validate if a year is valid
const isValidYear = (year: string | undefined | null): boolean => {
  if (!year) return false

  const parsedYear = parseInt(year, 10)
  const maxYear = getMaxVehicleYear()
  const minYear = 1990 // Minimum valid year

  return !isNaN(parsedYear) && parsedYear >= minYear && parsedYear <= maxYear
}

interface VehicleFormProps {
  open: boolean
  vehicle: Vehicle | null
  onClose: () => void
  onCancel?: () => void
  onSubmit: (data: { [key: string]: any }) => void | Promise<void | boolean>
  onReset?: () => void
  inventoryCollection: 'master_inventory' | 'shield_inventory'
  generateStockNumber: () => string
  isEdit?: boolean
  canViewPurchasePrice?: boolean
  canViewProfit?: boolean
  readOnly?: boolean
  /**
   * Prefill values when creating a vehicle (`vehicle === null`).
   * Applied after the empty create reset; skips session restore so lead/import
   * data is not overwritten by a stale draft.
   */
  initialValues?: Record<string, unknown>
}
function isValidURL(url: string) {
  try {
    new URL(url)
    return true
  } catch (e) {
    return false
  }
}

// FIXME: double declaration edit and isnew

// Vehicle Photos Section Component
const VehiclePhotosSection: React.FC<{ collectionName: string; vehicleId: string }> = ({ 
  collectionName, 
  vehicleId 
}) => {
  const { data: photos, isLoading } = usePhotosForInventoryObject(collectionName, vehicleId)
  const isMobile = useIsMobile()

  const handleOpenInNewTab = (url: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    window.open(url, '_blank')
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!photos || photos.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        height: 200,
        border: '2px dashed',
        borderColor: 'grey.300',
        borderRadius: 2,
        bgcolor: 'grey.50'
      }}>
        <PhotoCamera sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No photos available for this vehicle
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {photos.length} photo{photos.length !== 1 ? 's' : ''} available
        </Typography>
      </Box>
      
      <ImageList sx={{ width: '100%', height: 300 }} cols={isMobile ? 2 : 4} rowHeight={140}>
        {photos.map((photo: any) => (
          <ImageListItem key={photo.url} sx={{ position: 'relative' }}>
            <img
              srcSet={`${photo.url}?w=140&h=140&fit=crop&auto=format&dpr=2 2x`}
              src={`${photo.url}?w=140&h=140&fit=crop&auto=format`}
              alt={photo.name}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            
            {/* Position indicator badge for main photos */}
            {photo.position === 'main' && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  zIndex: 2,
                }}
              >
                MAIN
              </Box>
            )}
            
            {/* Position indicator for other photos */}
            {photo.position && photo.position !== 'main' && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  left: 4,
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  zIndex: 2,
                }}
              >
                {photo.position.toUpperCase()}
              </Box>
            )}
            
            {/* Open in new tab button */}
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 4, 
                right: 4,
                zIndex: 2
              }}
            >
              <IconButton
                size="small"
                sx={{ 
                  color: 'white', 
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' } 
                }}
                onClick={(e) => handleOpenInNewTab(photo.url, e)}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </Box>
          </ImageListItem>
        ))}
      </ImageList>
    </Box>
  )
}

const VehicleForm: React.FC<VehicleFormProps> = ({
  open,
  onClose,
  onCancel,
  onSubmit,
  onReset,
  vehicle,
  inventoryCollection,
  generateStockNumber,
  isEdit = false,
  canViewPurchasePrice = true,
  canViewProfit = true,
  readOnly = false,
  initialValues,
}) => {
  const { userInfo } = useAuth()
  const isMobile = useIsMobile()
  const mobileFormScrollSx = isMobile
    ? {
        overflowX: 'hidden',
        '& .MuiGrid-container > .MuiGrid-item': {
          flexBasis: '100%',
          maxWidth: '100%',
        },
        '& .MuiInputBase-input': { fontSize: '16px' },
        '& .MuiInputLabel-root': { fontSize: '16px' },
        '& .MuiSelect-select': { fontSize: '16px', py: 1.25 },
        '& .MuiButton-root': { minHeight: TOUCH_MIN },
        '& .MuiIconButton-root': { minWidth: TOUCH_MIN, minHeight: TOUCH_MIN },
      }
    : undefined
  const isNew = vehicle === null
  const isAdmin = userInfo?.role === 'admin'
  const isOwner = userInfo?.isOwner || false
  const isBabyAdmin = userInfo?.role === 'babyAdmin'
  const { vehicles, pauseSnapshotListener, resumeSnapshotListener, updateDocument, updateDocumentAsync } = useInventory(inventoryCollection)
  const minYear = inventoryCollection === 'shield_inventory' ? 1990 : 2000
  const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()
  const { checkPermission } = usePermissions()
  const canMarkForTransport = checkPermission('inventory', 'mark_for_transport')
  const canMarkSold = checkPermission('inventory', 'mark_sold')
  const canPurchaseWithoutPickupAddress = checkPermission('inventory', 'purchase_without_pickup_address')

  // Fetch saved pickup locations
  const { pickupLocations: savedPickupLocations, isLoading: isLoadingSavedLocations, createPickupLocation } = usePickupLocations()
  const { auctions, isLoading: isLoadingAuctions, createAuction } = useAuctions()
  
  const queryClient = useQueryClient()

  const [vinAlreadyExistsError, setVinAlreadyExistsError] = useState(false)
  const carriedAddVinRef = useRef<string | null>(null)
  const [previousVehicle, setPreviousVehicle] = useState<Vehicle | null>(null)
  const [isResell, setIsResell] = useState(false)
  const sizeSelectRef = useRef<HTMLDivElement>(null)
  const [isUploadingGatePass, setIsUploadingGatePass] = useState(false)
  const [isDeletingGatePass, setIsDeletingGatePass] = useState(false)
  const [gatePassPendingDelete, setGatePassPendingDelete] = useState<GatePassFile | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const gatePassFileInputRef = useRef<HTMLInputElement>(null)
  
  // State for save location dialog
  const [saveLocationDialogOpen, setSaveLocationDialogOpen] = useState(false)
  const [locationToSave, setLocationToSave] = useState<PickupLocation | null>(null)

  // State for add auction dialog
  const [auctionDialogOpen, setAuctionDialogOpen] = useState(false)
  
  // State for sold dialog
  const [soldDialogOpen, setSoldDialogOpen] = useState(false)

  // State for purchased-without-pickup-address notify dialog
  const [purchaseNotifyDialogOpen, setPurchaseNotifyDialogOpen] = useState(false)
  const [isSendingPickupNotify, setIsSendingPickupNotify] = useState(false)

  // State for field reset confirmation dialogs
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false)
  const [pendingFieldChange, setPendingFieldChange] = useState<{
    parentField: string
    parentValue: string
    dependentField: string
    dependentValue: string
    onConfirm: () => void
  } | null>(null)

  const vinCheckRef = useRef({ vehicle, vehicles })
  vinCheckRef.current = { vehicle, vehicles }

  const formSchema = z
    .object({
      stock_number: z.string().min(1, 'Stock Number is required'),
   vin: isAdmin
  ? z
    .string()
    .length(17, 'VIN must be exactly 17 characters long')
    .refine(val => {
      const current = vinCheckRef.current
      // Editing an existing truck is not a duplicate of itself.
      if (current.vehicle !== null) return true
      return (
        inventoryDuplicateMessage({ vin: val }, current.vehicles ?? []) == null
      )
    }, 'Vehicle with this VIN already exists in active inventory')
  : z.string().optional(),
      make: z.string().min(1, 'Make is required'),
      model: z.string().min(1, 'Model is required'),
      type: z.string().min(1, 'Type is required') as z.ZodType<Vehicle['type']>,
      size: z.string().min(1, 'Size is required') as z.ZodType<Vehicle['size']>,
      fuel: z.string().min(1, 'Fuel is required') as z.ZodType<Vehicle['fuel']>,
      miles: z
        .number()
        .min(0, 'Mileage must be a positive number')
        .refine(val => !isNaN(val) && val >= 0, 'Mileage must be a positive number')
        .or(z.undefined()) as z.ZodType<Vehicle['miles']>,
      year: z
        .number()
        .min(minYear, 'Year is required')
        .max(getMaxVehicleYear(), `Year must be between ${minYear} and ${getMaxVehicleYear()}`)
        .refine(val => !isNaN(val), 'Year must be a valid number')
        .refine(val => {
          const num = Number(val)
          return !isNaN(num) && num >= minYear && num <= getMaxVehicleYear()
        }, `Year must be between ${minYear} and ${getMaxVehicleYear()}`)
        .or(z.undefined()) as z.ZodType<Vehicle['year']>,
      gwvr_over_10k: z.enum(['yes', 'no', 'unknown']).default('unknown'),
      location: z
        .string()
        .min(1, 'Location is required')
        .refine(val => {
          // Skip validation if empty (the min(1) above will catch this)
          if (!val) return true
          // Check if it's a valid US state abbreviation
          return states.some(state => state.abbreviation === val)
        }, 'Please select a valid US state') as z.ZodType<Vehicle['location']>,
      condition: z.string().min(1, 'Condition is required') as z.ZodType<Vehicle['condition']>,
      color: z.enum(['White', 'Blue', 'Yellow', 'Being painted']).default('White') as z.ZodType<Vehicle['color']>,
      being_painted_date: z.string().optional().nullable() as z.ZodType<Vehicle['being_painted_date']>,
      status: z.string().min(1, 'Status is required') as z.ZodType<Vehicle['status']>,
      seller_type: z.string().optional() as z.ZodType<Vehicle['seller_type']>,
      auction_location_id: z.string().optional().nullable() as z.ZodType<Vehicle['auction_location_id']>,
      title_format: z
        .string()
        .optional()
        .nullable()
        .refine(
          val => !val || val === 'electronic' || val === 'paper',
          'Title format must be electronic or paper',
        ) as z.ZodType<Vehicle['title_format']>,
      exclude_from_title_emails: z.boolean().default(false),
      has_lien: z.boolean().optional().nullable() as z.ZodType<Vehicle['has_lien']>,
      lien_lender: z.string().optional().nullable() as z.ZodType<Vehicle['lien_lender']>,
      lien_payoff_amount: z
        .number()
        .optional()
        .nullable()
        .refine(
          val => !val || (!isNaN(Number(val)) && Number(val) >= 0),
          'Payoff amount must be a positive number',
        ) as z.ZodType<Vehicle['lien_payoff_amount']>,
      lien_good_through_date: z.string().optional().nullable() as z.ZodType<Vehicle['lien_good_through_date']>,
      lien_account_number: z.string().optional().nullable() as z.ZodType<Vehicle['lien_account_number']>,
      who: z.string().optional() as z.ZodType<Vehicle['who']>,
      phone_number: z
        .string()
        .optional()
        .refine(val => {
          if (!val) return true // Allow empty values
          // Remove all non-digit characters to count actual digits
          const digitsOnly = val.replace(/\D/g, '')
          // Ensure exactly 10 digits (standard US number)
          return digitsOnly.length === 10
        }, 'Phone number must be exactly 10 digits') as z.ZodType<Vehicle['phone_number']>,
      address: z.string().optional(),
      city: z.string().optional(),
      zip: z.string().optional(),
      // New pickup location fields
      pickup_address: z.string().optional(),
      pickup_city: z.string().optional(),
      pickup_state: z.string().optional(),
      pickup_zip: z.string().optional(),
      pickup_contact_name: z.string().optional(),
      pickup_contact_phone: z
        .string()
        .optional()
        .refine(val => {
          if (!val) return true // Allow empty values
          // Remove all non-digit characters to count actual digits
          const digitsOnly = val.replace(/\D/g, '')
          // Ensure exactly 10 digits (standard US number)
          return digitsOnly.length === 10
        }, 'Phone number must be exactly 10 digits'),
      pickup_notes: z.string().optional(),
      refinance: z.boolean().optional(),
      link: z
        .string()
        .optional()
        .refine(val => !val || isValidURL(val), 'URL is not valid') as z.ZodType<Vehicle['link']>,
      optic_list_price: z
        .number()
        .optional()
        .refine(
          val => !val || (!isNaN(Number(val)) && Number(val) >= 0),
          'Optic List Price must be a positive number',
        ) as z.ZodType<Vehicle['optic_list_price']>,
      seller_asking_price: isAdmin
        ? z
          .number()
          .refine(
            val => !val || (!isNaN(Number(val)) && Number(val) >= 0),
            'Seller Asking Price must be a positive number',
          )
        : (z.number().optional() as z.ZodType<Vehicle['seller_asking_price']>),
      sold_date: z.string().nullable().optional(),
      date_available: z.string().nullable().optional(),
      truck_notes: z.string().optional() as z.ZodType<Vehicle['truck_notes']>,
      estimated_body: z
        .number()
        .optional()
        .refine(
          val => !val || (!isNaN(Number(val)) && Number(val) >= 0),
          'Estimated body cost must be a positive number',
        ) as z.ZodType<Vehicle['estimated_body']>,
      estimated_mechanical: z
        .number()
        .optional()
        .refine(
          val => !val || (!isNaN(Number(val)) && Number(val) >= 0),
          'Estimated mechanical cost must be a positive number',
        ) as z.ZodType<Vehicle['estimated_mechanical']>,
      estimated_safety: z
        .number()
        .optional()
        .refine(
          val => !val || (!isNaN(Number(val)) && Number(val) >= 0),
          'Estimated safety cost must be a positive number',
        ) as z.ZodType<Vehicle['estimated_safety']>,
      estimated_repair_cost: z
        .number()
        .optional()
        .nullable() as z.ZodType<Vehicle['estimated_repair_cost']>,
      estimated_repair_addons: z
        .array(
          z.object({
            id: z.string(),
            amount: z.number(),
            category: z.enum(['body', 'mechanical', 'safety']),
            quantity: z.number().int().min(1).max(20).optional(),
          }),
        )
        .optional()
        .default([]) as z.ZodType<Vehicle['estimated_repair_addons']>,
      dealQuality: z.string().optional() as z.ZodType<Vehicle['dealQuality']>,
      cameras: z.enum(['yes', 'no', 'unknown']).default('unknown'),
      shelves: z.enum(['yes', 'no', 'unknown']).default('unknown'),
      size_confirmed: z.boolean().default(false),
      bought_from: z.string().optional(),
      purchased_for_inventory: z.boolean().default(false),
      paid_for: z.boolean().default(false),
      paid_for_date: z.string().nullable().optional() as z.ZodType<Vehicle['paid_for_date']>,
      funded: z.boolean().default(false),
      funded_date: z.string().nullable().optional() as z.ZodType<Vehicle['funded_date']>,
      translease_lease: z.boolean().default(false),
      delivered_to_customer: z.boolean().default(false),
      rear_wheel: z.enum(['SRW', 'DRW']).optional()
        .refine(val => {
          // Only require this field if type is Cutaway
          const type = watch ? watch('type') : null;
          if (type === 'Cutaway') {
            return !!val;
          }
          return true;
        }, 'Rear Wheel configuration is required for Cutaways'),
    })
    .refine(
      data => {
        // Skip validation if model or make is empty
        if (!data.model || !data.make) return true

        const validModels = getVehicleModelOptions(data.make)
        return validModels.includes(data.model) || data.model === 'Unknown'
      },
      {
        message: 'Model is not valid for the selected make',
        path: ['model'],
      },
    )
    .refine(
      data => {
        // Skip validation if size or type is empty
        if (!data.size || !data.type) return true

        const validSizes = getVehicleSizeOptions(data.type)
        return validSizes.includes(data.size) || data.size === 'Unknown'
      },
      {
        message: 'Size is not valid for the selected type',
        path: ['size'],
      },
    )
    .refine(
      data => {
        if (!data.purchased_for_inventory) return true
        return data.title_format === 'electronic' || data.title_format === 'paper'
      },
      {
        message: 'Title format is required',
        path: ['title_format'],
      },
    )


  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors, isSubmitting },
    trigger,
  } = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit', // ONLY CHANGE THIS LINE - validate only on submit
  })

  // Watch make and type to reset dependent fields when they change
  const watchedMake = watch('make')
  const watchedType = watch('type')
  const watchedStatus = watch('status')
  const watchedEstimatedBody = watch('estimated_body')
  const watchedEstimatedMechanical = watch('estimated_mechanical')
  const watchedEstimatedSafety = watch('estimated_safety')
  const watchedEstimatedRepairAddons = (watch('estimated_repair_addons') ||
    []) as AppliedEstimatedRepairAddon[]
  const { additionalExpenseItems } = useExpenseSettings({ dontShowUpdates: true })
  const estimatedRepairCostTotal = (() => {
    const body = watchedEstimatedBody
    const mechanical = watchedEstimatedMechanical
    const safety = watchedEstimatedSafety
    const hasAny =
      (body != null && !Number.isNaN(Number(body))) ||
      (mechanical != null && !Number.isNaN(Number(mechanical))) ||
      (safety != null && !Number.isNaN(Number(safety)))
    if (!hasAny) return vehicle?.estimated_repair_cost ?? null
    return (Number(body) || 0) + (Number(mechanical) || 0) + (Number(safety) || 0)
  })()

  const toggleEstimatedRepairAddon = (
    item: Pick<AdditionalExpenseItem, 'id' | 'amount' | 'category' | 'name'>,
    checked: boolean,
  ) => {
    if (readOnly) return

    const fields = {
      estimated_body: watch('estimated_body'),
      estimated_mechanical: watch('estimated_mechanical'),
      estimated_safety: watch('estimated_safety'),
      estimated_repair_addons: (watch('estimated_repair_addons') ||
        []) as AppliedEstimatedRepairAddon[],
    }
    const next = toggleRepairAddonOnFields(fields, item, checked)
    setValue('estimated_body', next.estimated_body ?? null, { shouldDirty: true })
    setValue('estimated_mechanical', next.estimated_mechanical ?? null, { shouldDirty: true })
    setValue('estimated_safety', next.estimated_safety ?? null, { shouldDirty: true })
    setValue('estimated_repair_addons', next.estimated_repair_addons ?? [], { shouldDirty: true })
  }

  const changeEstimatedRepairAddonQuantity = (addonId: string, quantity: number) => {
    if (readOnly) return

    const fields = {
      estimated_body: watch('estimated_body'),
      estimated_mechanical: watch('estimated_mechanical'),
      estimated_safety: watch('estimated_safety'),
      estimated_repair_addons: (watch('estimated_repair_addons') ||
        []) as AppliedEstimatedRepairAddon[],
    }
    const next = updateRepairAddonQuantityOnFields(fields, addonId, quantity)
    setValue('estimated_body', next.estimated_body ?? null, { shouldDirty: true })
    setValue('estimated_mechanical', next.estimated_mechanical ?? null, { shouldDirty: true })
    setValue('estimated_safety', next.estimated_safety ?? null, { shouldDirty: true })
    setValue('estimated_repair_addons', next.estimated_repair_addons ?? [], { shouldDirty: true })
  }

  const estimatedRepairAddonOptions = useMemo(() => {
    const catalogById = new Map(additionalExpenseItems.map(item => [item.id, item]))
    const appliedMissing = watchedEstimatedRepairAddons
      .filter(addon => !catalogById.has(addon.id))
      .map(addon => ({
        id: addon.id,
        name: 'Removed item',
        amount: addon.amount,
        category: addon.category,
        isRemovedFromCatalog: true,
      }))

    return [
      ...additionalExpenseItems.map(item => ({ ...item, isRemovedFromCatalog: false })),
      ...appliedMissing,
    ]
  }, [additionalExpenseItems, watchedEstimatedRepairAddons])

  const {
  saveFormData,
  clearFormData,
  restoreFormData,
  hasUserData
} = useVehicleFormSession(inventoryCollection, open, isNew, vehicle?.id)
const saveTimeoutRef = useRef<NodeJS.Timeout>()
const isSubmittingRef = useRef(false)



const hasRestoredRef = useRef(false)
useEffect(() => {
  if (!open) {
    hasRestoredRef.current = false
    return
  }
  pauseSnapshotListener()

  if (isNew) {
  formReset()
  setValue('stock_number', generateStockNumber())

  if (initialValues && Object.keys(initialValues).length > 0) {
    // Lead/import prefill wins over any leftover create-session draft.
    Object.entries(initialValues).forEach(([key, value]) => {
      if (value !== undefined) {
        setValue(key as any, value as any, { shouldDirty: false })
      }
    })
    hasRestoredRef.current = true
  } else if (hasUserData && !hasRestoredRef.current) {
    // STEP 3: Restore session data if available
    setTimeout(() => {
      restoreFormData(setValue)
      hasRestoredRef.current = true
    }, 0)
  }
    // Auto-save on changes
    const subscription = watch((data) => {
      if (isSubmittingRef.current) return
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = setTimeout(() => saveFormData(data as any), 500)
    })
    // Save on page unload
    const handleBeforeUnload = () => {
      if (isSubmittingRef.current) return
      const currentData = watch()
      if (currentData) {
        const { stock_number, ...dataToSave } = currentData
        sessionStorage.setItem(`vehicle_form_data_${inventoryCollection}`, JSON.stringify(dataToSave))
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      subscription.unsubscribe()
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      resumeSnapshotListener()
    }
  } 
  
  else if (vehicle) {
  hasRestoredRef.current = false // Reset for edit mode
  reset({
    stock_number: vehicle.stock_number || '',
    vin: vehicle.vin || '',
    make: vehicle.make || '',
    model: vehicle.model || '',
    type: vehicle.type || '',
    size: vehicle.size || '',
    fuel: vehicle.fuel || '',
    miles: vehicle.miles,
    year: vehicle.year,
    gwvr_over_10k: vehicle.gwvr_over_10k || 'unknown',
    location: vehicle.location || '',
    condition: vehicle.condition || '',
    color: vehicle.color || 'White',
    being_painted_date: vehicle.being_painted_date ?? null,
    status: vehicle.status || '',
    seller_type: vehicle.seller_type || '',
    auction_location_id: vehicle.auction_location_id || '',
    title_format: vehicle.title_format || '',
    exclude_from_title_emails: Boolean(vehicle.exclude_from_title_emails),
    has_lien: vehicle.has_lien ?? null,
    lien_lender: vehicle.lien_lender || '',
    lien_payoff_amount: vehicle.lien_payoff_amount,
    lien_good_through_date: vehicle.lien_good_through_date || null,
    lien_account_number: vehicle.lien_account_number || '',
    who: vehicle.who || '',
    phone_number: vehicle.phone_number || '',
    address: vehicle.pickup_location?.address || '',
    city: vehicle.pickup_location?.city || '',
    zip: vehicle.pickup_location?.zip || '',
    // Pickup location fields
    pickup_address: vehicle.pickup_location?.address || '',
    pickup_city: vehicle.pickup_location?.city || '',
    pickup_state: vehicle.pickup_location?.state || '',
    pickup_zip: vehicle.pickup_location?.zip || '',
    pickup_contact_name: vehicle.pickup_location?.contact_name || '',
    pickup_contact_phone: vehicle.pickup_location?.contact_phone || '',
    pickup_notes: vehicle.pickup_location?.notes || '',
    refinance: Boolean(vehicle.refinance),
    link: vehicle.link || '',
    optic_list_price: vehicle.optic_list_price,
    seller_asking_price: vehicle.seller_asking_price,
    sold_date: vehicle.sold_date || null,
    date_available: vehicle.date_available || null,
    truck_notes: vehicle.truck_notes || '',
    estimated_body: vehicle.estimated_body ?? null,
    estimated_mechanical: vehicle.estimated_mechanical ?? null,
    estimated_safety: vehicle.estimated_safety ?? null,
    estimated_repair_cost: vehicle.estimated_repair_cost ?? null,
    estimated_repair_addons: vehicle.estimated_repair_addons || [],
    cameras: vehicle.cameras || 'unknown',
    shelves: vehicle.shelves || 'unknown',
    size_confirmed: Boolean(vehicle.size_confirmed),
    bought_from: vehicle.bought_from || '',
    purchased_for_inventory: Boolean(vehicle.purchased_for_inventory),
    paid_for: Boolean(vehicle.paid_for),
    paid_for_date: vehicle.paid_for_date || null,
    funded: Boolean(vehicle.funded),
    funded_date: vehicle.funded_date || null,
    translease_lease: Boolean(vehicle.translease_lease),
    delivered_to_customer: Boolean(vehicle.delivered_to_customer),
    rear_wheel: vehicle.rear_wheel || undefined,
  })
  
  // ADD THIS EDIT SESSION STORAGE CODE:
  if (hasUserData && !hasRestoredRef.current) {
    setTimeout(() => {
      restoreFormData(setValue)
      hasRestoredRef.current = true
    }, 100)
  } else {
    hasRestoredRef.current = true // Allow form watching to start
  }

  // Auto-save on changes for edit mode
  const editSubscription = watch((data) => {
    if (!hasRestoredRef.current || isSubmittingRef.current) return
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = setTimeout(() => saveFormData(data as any), 500)
  })

  // Save on page unload for edit mode
  const handleEditBeforeUnload = () => {
    if (isSubmittingRef.current) return
    const currentData = watch()
    if (currentData) {
      saveFormData(currentData as any)
    }
  }
  
  window.addEventListener('beforeunload', handleEditBeforeUnload)
  
  // Cleanup for edit mode
  return () => {
    editSubscription.unsubscribe()
    window.removeEventListener('beforeunload', handleEditBeforeUnload)
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    resumeSnapshotListener()
  }
}
  return () => resumeSnapshotListener()
}, [open, isNew, vehicle?.id, initialValues])

const handleCancel = () => {
  if (isNew) {
    clearFormData()
    reset()
  } else {
    // For edit mode: clear session storage and restore original vehicle data
    clearFormData()
    
    // Reset form to original database values
    reset({
      stock_number: vehicle?.stock_number || '',
      vin: vehicle?.vin || '',
      make: vehicle?.make || '',
      model: vehicle?.model || '',
      type: vehicle?.type || '',
      size: vehicle?.size || '',
      fuel: vehicle?.fuel || '',
      miles: vehicle?.miles,
      year: vehicle?.year,
      gwvr_over_10k: vehicle?.gwvr_over_10k || 'unknown',
      location: vehicle?.location || '',
      condition: vehicle?.condition || '',
      color: vehicle?.color || 'White',
      being_painted_date: vehicle?.being_painted_date ?? null,
      status: vehicle?.status || '',
      seller_type: vehicle?.seller_type || '',
      auction_location_id: vehicle?.auction_location_id || '',
      title_format: vehicle?.title_format || '',
      exclude_from_title_emails: Boolean(vehicle?.exclude_from_title_emails),
      has_lien: vehicle?.has_lien ?? null,
      lien_lender: vehicle?.lien_lender || '',
      lien_payoff_amount: vehicle?.lien_payoff_amount,
      lien_good_through_date: vehicle?.lien_good_through_date || null,
      lien_account_number: vehicle?.lien_account_number || '',
      who: vehicle?.who || '',
      phone_number: vehicle?.phone_number || '',
      address: vehicle?.pickup_location?.address || '',
      city: vehicle?.pickup_location?.city || '',
      zip: vehicle?.pickup_location?.zip || '',
      // Pickup location fields
      pickup_address: vehicle?.pickup_location?.address || '',
      pickup_city: vehicle?.pickup_location?.city || '',
      pickup_state: vehicle?.pickup_location?.state || '',
      pickup_zip: vehicle?.pickup_location?.zip || '',
      pickup_contact_name: vehicle?.pickup_location?.contact_name || '',
      pickup_contact_phone: vehicle?.pickup_location?.contact_phone || '',
      pickup_notes: vehicle?.pickup_location?.notes || '',
      refinance: Boolean(vehicle?.refinance),
      link: vehicle?.link || '',
      optic_list_price: vehicle?.optic_list_price,
      seller_asking_price: vehicle?.seller_asking_price,
      sold_date: vehicle?.sold_date || null,
      date_available: vehicle?.date_available || null,
      truck_notes: vehicle?.truck_notes || '',
      estimated_body: vehicle?.estimated_body ?? null,
      estimated_mechanical: vehicle?.estimated_mechanical ?? null,
      estimated_safety: vehicle?.estimated_safety ?? null,
      estimated_repair_cost: vehicle?.estimated_repair_cost ?? null,
      estimated_repair_addons: vehicle?.estimated_repair_addons || [],
      cameras: vehicle?.cameras || 'unknown',
      shelves: vehicle?.shelves || 'unknown',
      size_confirmed: Boolean(vehicle?.size_confirmed),
      bought_from: vehicle?.bought_from || '',
      purchased_for_inventory: Boolean(vehicle?.purchased_for_inventory),
      paid_for: Boolean(vehicle?.paid_for),
      paid_for_date: vehicle?.paid_for_date || null,
      funded: Boolean(vehicle?.funded),
      funded_date: vehicle?.funded_date || null,
      translease_lease: Boolean(vehicle?.translease_lease),
      delivered_to_customer: Boolean(vehicle?.delivered_to_customer),
      rear_wheel: vehicle?.rear_wheel || undefined,
    })
  }
  onCancel ? onCancel() : onClose()
  }

const formReset = () => {
   reset({
      stock_number: generateStockNumber(),
      vin: vehicle?.vin || '',
      make: vehicle?.make || '',
      model: vehicle?.model || '',
      type: vehicle?.type || '',
      size: vehicle?.size || '',
      fuel: vehicle?.fuel || '',
      miles: vehicle?.miles,
      year: vehicle?.year,
      gwvr_over_10k: vehicle?.gwvr_over_10k || 'unknown',
      location: vehicle?.location || '',
      condition: vehicle?.condition || '',
      color: vehicle?.color || 'White',
      being_painted_date: vehicle?.being_painted_date ?? null,
      status: vehicle?.status || '',
      seller_type: vehicle?.seller_type || '',
      auction_location_id: vehicle?.auction_location_id || '',
      title_format: vehicle?.title_format || '',
      exclude_from_title_emails: Boolean(vehicle?.exclude_from_title_emails),
      has_lien: vehicle?.has_lien ?? null,
      lien_lender: vehicle?.lien_lender || '',
      lien_payoff_amount: vehicle?.lien_payoff_amount,
      lien_good_through_date: vehicle?.lien_good_through_date || null,
      lien_account_number: vehicle?.lien_account_number || '',
      who: vehicle?.who || '',
      phone_number: vehicle?.phone_number || '',
      address: vehicle?.pickup_location?.address || '',
      city: vehicle?.pickup_location?.city || '',
      zip: vehicle?.pickup_location?.zip || '',
      // Pickup location fields
      pickup_address: vehicle?.pickup_location?.address || '',
      pickup_city: vehicle?.pickup_location?.city || '',
      pickup_state: vehicle?.pickup_location?.state || '',
      pickup_zip: vehicle?.pickup_location?.zip || '',
      pickup_contact_name: vehicle?.pickup_location?.contact_name || '',
      pickup_contact_phone: vehicle?.pickup_location?.contact_phone || '',
      pickup_notes: vehicle?.pickup_location?.notes || '',
      refinance: Boolean(vehicle?.refinance),
      link: vehicle?.link || '',
      optic_list_price: vehicle?.optic_list_price,
      seller_asking_price: vehicle?.seller_asking_price,
      sold_date: vehicle?.sold_date || null,
      date_available: vehicle?.date_available || null,
      truck_notes: vehicle?.truck_notes || '',
      estimated_body: vehicle?.estimated_body ?? null,
      estimated_mechanical: vehicle?.estimated_mechanical ?? null,
      estimated_safety: vehicle?.estimated_safety ?? null,
      estimated_repair_cost: vehicle?.estimated_repair_cost ?? null,
      estimated_repair_addons: vehicle?.estimated_repair_addons || [],
      cameras: vehicle?.cameras || 'unknown',
      shelves: vehicle?.shelves || 'unknown',
      size_confirmed: Boolean(vehicle?.size_confirmed),
      bought_from: vehicle?.bought_from || '',
      purchased_for_inventory: Boolean(vehicle?.purchased_for_inventory),
      paid_for: Boolean(vehicle?.paid_for),
      paid_for_date: vehicle?.paid_for_date || null,
      delivered_to_customer: Boolean(vehicle?.delivered_to_customer),
      rear_wheel: vehicle?.rear_wheel || undefined,
    })
}

  // Handle make change
  const handleMakeChange = (e: any) => {
    const newMake = e.target.value
    const currentModel = watch('model')

    // Check if model needs to be reset
    if (currentModel) {
      const validModels = getVehicleModelOptions(newMake)
      if (!validModels.includes(currentModel) && currentModel !== 'Unknown') {
        // Show confirmation dialog
        setPendingFieldChange({
          parentField: 'Make',
          parentValue: newMake,
          dependentField: 'Model',
          dependentValue: currentModel,
          onConfirm: () => {
            // Apply the changes after confirmation
            setValue('make', newMake)
            setValue('model', '')
            trigger(['make', 'model'])
          },
        })
        setResetConfirmationOpen(true)
        return
      }
    }

    // If no reset needed, just set the value
    setValue('make', newMake)
    trigger(['make'])
  }

  // Handle type change
  const handleTypeChange = (e: any) => {
    const newType = e.target.value
    const currentSize = watch('size')

    // Check if size needs to be reset
    if (currentSize) {
      const validSizes = getVehicleSizeOptions(newType)
      if (!validSizes.includes(currentSize) && currentSize !== 'Unknown') {
        // Show confirmation dialog
        setPendingFieldChange({
          parentField: 'Type',
          parentValue: newType,
          dependentField: 'Size',
          dependentValue: currentSize,
          onConfirm: () => {
            // Apply the changes after confirmation
            setValue('type', newType)
            setValue('size', '')
            trigger(['type', 'size'])
          },
        })
        setResetConfirmationOpen(true)
        return
      }
    }

    // If no reset needed, just set the value
    setValue('type', newType)
    trigger(['type'])
  }

  // Handle confirmation dialog close
  const handleConfirmationClose = () => {
    setResetConfirmationOpen(false)
    setPendingFieldChange(null)
  }

  // Handle confirmation dialog confirm
  const handleConfirmationConfirm = () => {
    if (pendingFieldChange) {
      pendingFieldChange.onConfirm()
    }
    setResetConfirmationOpen(false)
    setPendingFieldChange(null)
  }

  // Handle selecting a previous pickup address
  const handlePreviousAddressSelect = (selectedAddress: PickupLocation | null) => {
    if (selectedAddress) {
      setValue('pickup_address', selectedAddress.address)
      setValue('pickup_city', selectedAddress.city)
      setValue('pickup_state', selectedAddress.state)
      setValue('pickup_zip', selectedAddress.zip)
      setValue('pickup_contact_phone', selectedAddress.contact_phone || '')
      setValue('pickup_notes', selectedAddress.notes || '')
      
      // Trigger validation for all pickup fields
      trigger(['pickup_address', 'pickup_city', 'pickup_state', 'pickup_zip', 'pickup_contact_name', 'pickup_contact_phone', 'pickup_notes'])
    }
  }

  // Check if location has enough data to be saved
  const canSaveLocation = (): boolean => {
    const pickupAddress = watch('pickup_address')?.trim() || ''
    const pickupCity = watch('pickup_city')?.trim() || ''
    const pickupState = watch('pickup_state')?.trim() || ''
    const pickupZip = watch('pickup_zip')?.trim() || ''
    return !!(pickupAddress && pickupCity && pickupState && pickupZip)
  }

  // Check if location already exists in saved locations
  const locationExists = (): boolean => {
    if (!canSaveLocation()) {
      return false
    }

    const normalize = (str: string | undefined) => (str || '').trim().toLowerCase()
    const pickupAddress = normalize(watch('pickup_address'))
    const pickupCity = normalize(watch('pickup_city'))
    const pickupState = normalize(watch('pickup_state'))
    const pickupZip = normalize(watch('pickup_zip'))
    const pickupContactName = normalize(watch('pickup_contact_name'))
    const pickupContactPhone = normalize(watch('pickup_contact_phone'))

    // Check against all saved locations
    return (savedPickupLocations || []).some(savedLocation => {
      const savedAddress = normalize(savedLocation.address)
      const savedCity = normalize(savedLocation.city)
      const savedState = normalize(savedLocation.state)
      const savedZip = normalize(savedLocation.zip)
      const savedContactName = normalize(savedLocation.contact_name)
      const savedContactPhone = normalize(savedLocation.contact_phone)

      // Match on core address fields (required)
      const addressMatches = 
        pickupAddress === savedAddress &&
        pickupCity === savedCity &&
        pickupState === savedState &&
        pickupZip === savedZip

      if (!addressMatches) {
        return false
      }

      // If contact info is provided in the new location, it should match
      // If contact info is not provided, it's still a match (more lenient)
      const contactNameMatches = 
        !pickupContactName || 
        !savedContactName || 
        pickupContactName === savedContactName
      
      const contactPhoneMatches = 
        !pickupContactPhone || 
        !savedContactPhone || 
        pickupContactPhone === savedContactPhone

      return contactNameMatches && contactPhoneMatches
    })
  }

  // Handle opening save location dialog
  const handleOpenSaveLocation = () => {
    const locationData: PickupLocation = {
      address: watch('pickup_address') || '',
      city: watch('pickup_city') || '',
      state: watch('pickup_state') || '',
      zip: watch('pickup_zip') || '',
      contact_name: watch('pickup_contact_name') || '',
      contact_phone: watch('pickup_contact_phone') || '',
      notes: watch('pickup_notes') || '',
    }
    setLocationToSave(locationData)
    setSaveLocationDialogOpen(true)
  }

  // Handle saving location from dialog
  const handleSaveLocation = async (data: any) => {
    try {
      await createPickupLocation(data)
      // Invalidate queries to refresh autocomplete
      queryClient.invalidateQueries({ queryKey: ['pickupLocations'] })
      showSuccess('Location saved successfully')
      setSaveLocationDialogOpen(false)
      setLocationToSave(null)
    } catch (error) {
      console.error('Error saving location:', error)
      showError('Error saving location')
    }
  }

  const handleCreateAuction = async (input: { name: string; gate_passes_needed: boolean }) => {
    const created = await createAuction(input)
    if (created?.id) {
      setValue('auction_location_id', created.id, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
    }
  }

  // Handle gate pass file upload
  const handleGatePassUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !vehicle?.id) return

    setIsUploadingGatePass(true)
    setUploadProgress(0)

    try {
      const uploadedFiles: GatePassFile[] = []
      const totalFiles = files.length

      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        
        try {
          // Sanitize filename
          const fileExtension = file.name.split('.').pop() || ''
          const baseFileName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name
          const sanitizedBaseName = baseFileName.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_{2,}/g, '_')
          const timestamp = Date.now()
          const fileName = `${timestamp}_${sanitizedBaseName}${fileExtension ? '.' + fileExtension : ''}`
          
          const storagePath = `${inventoryCollection}/${vehicle.id}/gate_passes/${fileName}`
          
          if (!storage) {
            throw new Error('Firebase Storage is not initialized')
          }
          
          const storageRef = ref(storage, storagePath)
          const uploadResult = await uploadBytes(storageRef, file)
          const url = await getDownloadURL(uploadResult.ref)
          
          const gatePassFile: GatePassFile = {
            fileName: file.name,
            storagePath,
            url,
            fileSize: file.size,
            fileType: file.type,
            uploadedAt: new Date().toISOString(),
            uploadedBy: userInfo?.id || vehicle.entered_by || 'unknown',
          }
          
          uploadedFiles.push(gatePassFile)
          
          setUploadProgress(Math.round(((i + 1) / totalFiles) * 100))
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error)
          showError(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`)
        }
      }

      if (uploadedFiles.length > 0) {
        const existingFiles = (vehicle.gate_passes || []) as GatePassFile[]
        const updatedFiles = [...existingFiles, ...uploadedFiles]

        // Use updateDocument from useInventory hook for consistency
        await updateDocument({
          id: vehicle.id,
          data: {
            gate_passes: updatedFiles,
            updated_at: Timestamp.now(),
          }
        })

        showSuccess(`Successfully uploaded ${uploadedFiles.length} file(s)`)
      } else {
        showWarning('No files were successfully uploaded')
      }
    } catch (error) {
      console.error('Error uploading gate pass files:', error)
      showError(`Error uploading files: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsUploadingGatePass(false)
      setUploadProgress(0)
      if (gatePassFileInputRef.current) {
        gatePassFileInputRef.current.value = ''
      }
    }
  }

  const handleGatePassFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      handleGatePassUpload(files)
    }
  }

  const triggerGatePassFileInput = () => {
    if (gatePassFileInputRef.current) {
      gatePassFileInputRef.current.click()
    }
  }

  const handleGatePassDelete = async (file: GatePassFile) => {
    if (!vehicle?.id || readOnly) return

    setIsDeletingGatePass(true)
    try {
      if (storage && file.storagePath) {
        try {
          await deleteObject(ref(storage, file.storagePath))
        } catch (storageError) {
          // Missing object should not block removing the Firestore reference.
          const code =
            storageError && typeof storageError === 'object' && 'code' in storageError
              ? String((storageError as { code?: string }).code)
              : ''
          if (code !== 'storage/object-not-found') {
            throw storageError
          }
        }
      }

      const existingFiles = (vehicle.gate_passes || []) as GatePassFile[]
      const updatedFiles = existingFiles.filter(
        (entry) =>
          entry.storagePath !== file.storagePath &&
          !(entry.storagePath == null && entry.url === file.url && entry.fileName === file.fileName),
      )

      await updateDocument({
        id: vehicle.id,
        data: {
          gate_passes: updatedFiles,
          updated_at: Timestamp.now(),
        },
      })

      showSuccess(`Removed ${file.fileName}`)
    } catch (error) {
      console.error('Error deleting gate pass file:', error)
      showError(
        `Failed to remove ${file.fileName}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    } finally {
      setIsDeletingGatePass(false)
      setGatePassPendingDelete(null)
    }
  }

  const formatUploadDate = (dateString: string | Date | undefined): string => {
    if (!dateString) return 'Unknown'
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return 'Unknown'
    }
  }

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return '🖼️'
    if (fileType.startsWith('video/')) return '🎥'
    if (fileType.includes('pdf')) return '📄'
    if (fileType.includes('word') || fileType.includes('doc')) return '📝'
    if (fileType.includes('excel') || fileType.includes('sheet')) return '📊'
    return '📎'
  }

  // Add a function to format phone number input
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = e.target.value.replace(/\D/g, '') // Remove non-digits
    let formatted = ''

    if (input.length <= 3) {
      formatted = input
    } else if (input.length <= 6) {
      formatted = `(${input.slice(0, 3)}) ${input.slice(3)}`
    } else {
      formatted = `(${input.slice(0, 3)}) ${input.slice(3, 6)}-${input.slice(6, 10)}`
    }

    // Update the field value
    setValue('phone_number', formatted, { shouldValidate: true })
  }


  const { grayBookRetailPrice, grayBookWholeSalePrice, dealQuality, dealQualityInfo, matchedFormula } =
    useCalculateDeal({
      make: watch('make'),
      model: watch('model'),
      type: watch('type'),
      size: watch('size'),
      miles: watch('miles'),
      year: watch('year'),
      sellerAskingPrice: watch('seller_asking_price'),
      fuel: watch('fuel'),
    })

  const vin = watch('vin') || ''

  // Memoize pickup location values to prevent flickering
  const pickupAddress = watch('pickup_address')
  const pickupCity = watch('pickup_city')
  const pickupState = watch('pickup_state')
  const pickupZip = watch('pickup_zip')
  const pickupContactName = watch('pickup_contact_name')
  const pickupContactPhone = watch('pickup_contact_phone')
  const isRefinance = Boolean(watch('refinance'))
  const [pickupContactInput, setPickupContactInput] = useState('')

  // Keep Autocomplete inputValue in sync with RHF (needed for initialValues / edit load).
  useEffect(() => {
    setPickupContactInput(pickupContactName || '')
  }, [vehicle?.id, pickupContactName])
  
  const pickupLocationState = useMemo(() => {
    const address = pickupAddress?.trim() || ''
    const city = pickupCity?.trim() || ''
    const state = pickupState?.trim() || ''
    const zip = pickupZip?.trim() || ''
    const contactName = pickupContactName?.trim() || ''
    const contactPhone = pickupContactPhone?.trim() || ''
    
    const isAddressComplete = !!(address && city && state && zip)
    const isComplete = !!(address && city && state && zip && contactName && contactPhone)
    const isIncomplete = !isComplete
    
    return { isComplete, isIncomplete, isAddressComplete }
  }, [pickupAddress, pickupCity, pickupState, pickupZip, pickupContactName, pickupContactPhone])

  const auctionOptions = useMemo(
    () => [...(auctions || [])].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [auctions],
  )

  const getAuctionLabel = (auction: { id?: string; name?: string }) =>
    auction.name?.trim() || auction.id || 'Auction'

  useEffect(() => {
    const carried = nextCarriedAddVin(carriedAddVinRef.current, open, isNew, vin)
    carriedAddVinRef.current = carried.carriedVin

    setVinAlreadyExistsError(false)
    setPreviousVehicle(null)
    setIsResell(false)

    // The inventory list updates after a create while this dialog is still
    // mounted. Only an add form should warn, and never about the open truck.
    // Also skip the VIN still sitting in the field from the edit form we just left.
    if (!open || !isNew || carried.ignore || vin.length !== 17) return

    const existingVehicles = vehicles?.filter(v => v.vin === vin) || []

    if (existingVehicles.length > 0) {
        // Check if any of the vehicles with this VIN are not sold (active inventory)
        const activeVehicles = existingVehicles.filter(
          v => v.status?.toLowerCase() !== 'sold' && v.status?.toLowerCase() !== 'sold elsewhere',
        )

        if (activeVehicles.length > 0) {
          // There are active vehicles with this VIN - show error
          setVinAlreadyExistsError(true)
        } else {
          // All vehicles with this VIN are sold - this is a resell
          // Sort by sold date to get the most recent one
          const sortedVehicles = [...existingVehicles].sort((a, b) => {
            if (!a.sold_date && !b.sold_date) return 0
            if (!a.sold_date) return 1
            if (!b.sold_date) return -1
            return new Date(b.sold_date).getTime() - new Date(a.sold_date).getTime()
          })

          const latestVehicle = sortedVehicles[0]
          setIsResell(true)
          setPreviousVehicle(latestVehicle)
        }
      } else {
        // No existing vehicle with this VIN, fetch VIN data as usual
        fetchVinData(vin)
          .then(vinData => {
            const vinMake = vinData.find(item => item.Variable === 'Make')?.Value
            const vinYear = vinData.find(item => item.Variable === 'Model Year')?.Value
            const gvwrStatus = isGvwrOver10k(
              vinData.find(item => item.Variable === 'Gross Vehicle Weight Rating From')?.Value || '',
            )

            // Map the VIN make to our allowed make values
            const mappedMake = mapVinMakeToAllowedMake(vinMake)

            // Only set the make if it's in our allowed values
            if (mappedMake) {
              setValue('make', mappedMake)
            }

            // Only set the year if it's valid
            if (isValidYear(vinYear)) {
              setValue('year', parseInt(vinYear || '0'))
            }

            setValue('gwvr_over_10k', gvwrStatus)
          })
          .catch(error => {
            console.error('Error fetching VIN data:', error)
          })
      }
  }, [open, isNew, vin, isEdit, setValue, vehicles])

  // Function to populate the form with data from the previous vehicle
  const populateFromPreviousVehicle = () => {
    if (!previousVehicle) return

    // Update all relevant fields from the previous vehicle
    setValue('make', previousVehicle.make || '')
    setValue('model', previousVehicle.model || '')
    setValue('type', previousVehicle.type || '')
    setValue('size', previousVehicle.size || '')
    setValue('fuel', previousVehicle.fuel || '')
    setValue('year', previousVehicle.year || undefined)
    setValue('cameras', previousVehicle.cameras || 'unknown')
    setValue('shelves', previousVehicle.shelves || 'unknown')
    setValue('size_confirmed', Boolean(previousVehicle.size_confirmed))

    // Only set location if not already set
    if (!watch('location') && previousVehicle.location) {
      setValue('location', previousVehicle.location)
    }

    // Update miles only if the vehicle is in "Used" condition
    setValue('condition', 'Used')

    // Populate the optic list price from the previous vehicle
    if (previousVehicle.optic_list_price) {
      setValue('optic_list_price', previousVehicle.optic_list_price)
    }

    // Add note about resell
    const existingNotes = watch('truck_notes') || ''
    const resellNote = `[RESELL] This vehicle was previously sold (Stock #${previousVehicle.stock_number}).`
    setValue('truck_notes', existingNotes ? `${existingNotes}\n${resellNote}` : resellNote)

    trigger() // Trigger validation
  }

const onSubmitForm = async (data: any) => {
  // Sync lock before any await — RHF isSubmitting alone can miss a double-click.
  if (isSubmittingRef.current) return
  isSubmittingRef.current = true

  // Set defaults for required fields
  if (!data.cameras) data.cameras = 'unknown'
  if (!data.shelves) data.shelves = 'unknown'
  data.refinance = Boolean(data.refinance)
  data.translease_lease = Boolean(data.translease_lease)
  data.exclude_from_title_emails = Boolean(data.exclude_from_title_emails)
  data.estimated_repair_addons = Array.isArray(data.estimated_repair_addons)
    ? data.estimated_repair_addons
    : []

  const hasEstimatedCategory =
    (data.estimated_body != null && !Number.isNaN(Number(data.estimated_body))) ||
    (data.estimated_mechanical != null && !Number.isNaN(Number(data.estimated_mechanical))) ||
    (data.estimated_safety != null && !Number.isNaN(Number(data.estimated_safety)))
  if (hasEstimatedCategory) {
    data.estimated_repair_cost =
      (Number(data.estimated_body) || 0) +
      (Number(data.estimated_mechanical) || 0) +
      (Number(data.estimated_safety) || 0)
  } else {
    const vehicleHadCategories =
      vehicle?.estimated_body != null ||
      vehicle?.estimated_mechanical != null ||
      vehicle?.estimated_safety != null
    // Preserve legacy totals until categories are entered; clear once categories were used.
    data.estimated_repair_cost = vehicleHadCategories
      ? null
      : (vehicle?.estimated_repair_cost ?? null)
  }

  const wasNotSold = (vehicle?.status || '').slice(0, 4).toLowerCase() !== 'sold'
  const isSoldNow = data.status?.toString().toLowerCase() === 'sold'
  const isSoldElsewhereNow = data.status?.toString().toLowerCase() === 'sold elsewhere'

  if (isSoldNow && !data.sold_date && (!vehicle || !vehicle.sold_date)) {
    const today = new Date()
    today.setHours(12, 0, 0, 0)
    data.sold_date = today.toISOString()
  }

  if (isSoldNow || isSoldElsewhereNow) {
    data.show_on_website = false
  }

  // Create pickup_location object from dedicated pickup fields
  const pickup_location = {
    address: data.pickup_address || '',
    city: data.pickup_city || '',
    state: data.pickup_state || data.location || '', // Use pickup_state or fall back to vehicle location
    zip: data.pickup_zip || '',
    contact_name: data.pickup_contact_name || '',
    contact_phone: data.pickup_contact_phone || '',
    notes: data.pickup_notes || ''
  }

  // Check if pickup location is complete (address fields only)
  const pickupAddress = pickup_location.address?.trim() || ''
  const pickupCity = pickup_location.city?.trim() || ''
  const pickupState = pickup_location.state?.trim() || ''
  const pickupZip = pickup_location.zip?.trim() || ''

  const isPickupLocationComplete = pickupAddress && pickupCity && pickupState && pickupZip

  // Set ready_for_transport automatically from paid status + complete pickup location.
  // Preserve existing ready_for_pickup so manual marks remain persisted.
  const currentReadyForTransport = vehicle?.ready_for_transport || { needs_address: false, ready_for_pickup: false }
  const isPaidFor = data.paid_for === true
  const pickupWasComplete = Boolean(
    vehicle?.pickup_location?.address?.trim() &&
      vehicle?.pickup_location?.city?.trim() &&
      vehicle?.pickup_location?.state?.trim() &&
      vehicle?.pickup_location?.zip?.trim(),
  )
  const paidJustEnabled = isPaidFor && vehicle?.paid_for !== true
  const addressJustCompleted = Boolean(isPickupLocationComplete) && !pickupWasComplete
  // Do not re-mark a truck that was unmarked while it was already paid with an address.
  const autoReadyForPickup = Boolean(
    isPaidFor && isPickupLocationComplete && (paidJustEnabled || addressJustCompleted),
  )
  const keepManualReadyForPickup = currentReadyForTransport.ready_for_pickup === true
  const readyForPickup = Boolean(autoReadyForPickup || keepManualReadyForPickup)
  const readyForTransport: any = {
    needs_address: isPickupLocationComplete ? false : (currentReadyForTransport.needs_address || false),
    ready_for_pickup: readyForPickup,
  }
  if (currentReadyForTransport.note) {
    readyForTransport.note = currentReadyForTransport.note
  }
  if (readyForPickup) {
    readyForTransport.ready_for_transport_date =
      currentReadyForTransport.ready_for_transport_date || new Date().toISOString()
  } else {
    readyForTransport.ready_for_transport_date = null
  }

  // Auto-advance title_status to payment_sent when paid_for is set to true
  // Only if vehicle is purchased_for_inventory and current status is bank_information or earlier
  const currentTitleStatus = vehicle?.title_tracking?.title_status
  const statusOrder: TitleStatus[] = [
    'purchased_for_inventory',
    'bank_information',
    'payment_sent',
    'title_requested',
    'in_transit',
    'physical_title_received',
    'title_flip_required',
    'title_flip_not_required',
    'title_flip_in_progress',
    'title_flip_completed',
    'shipping_information',
    'title_shipped',
    'completed',
  ]
  const currentStatusIndex = currentTitleStatus ? statusOrder.indexOf(currentTitleStatus) : -1
  const paymentSentIndex = statusOrder.indexOf('payment_sent')
  
  const shouldAutoAdvancePayment = data.paid_for === true && 
    vehicle?.purchased_for_inventory === true &&
    (currentStatusIndex === -1 || currentStatusIndex < paymentSentIndex) &&
    currentTitleStatus !== 'payment_sent'

  // Set purchased_for_inventory_date at top level when "Purchased for Inventory" is toggled (not part of title flow)
  const purchasedForInventoryDate =
    data.purchased_for_inventory === true
      ? (vehicle?.purchased_for_inventory_date || new Date().toISOString())
      : data.purchased_for_inventory === false
        ? null
        : undefined

  // Set being_painted_date when color is "Being painted" (keep existing date or set to now)
  const beingPaintedDate =
    data.color === 'Being painted'
      ? (vehicle?.being_painted_date || data.being_painted_date || new Date().toISOString())
      : undefined

  let titleTrackingUpdate: Record<string, unknown> | undefined
  if (shouldAutoAdvancePayment) {
    titleTrackingUpdate = {
      ...vehicle?.title_tracking,
      title_status: 'payment_sent',
      paid_date: data.paid_for_date || new Date().toISOString(),
      last_updated_at: new Date().toISOString(),
      last_updated_by: userInfo?.email || 'Unknown',
    }
  }

  const finalData = {
    ...data,
    pickup_location,
    ready_for_transport: readyForTransport,
    auction_location_id:
      data.seller_type === 'Auction' && typeof data.auction_location_id === 'string' && data.auction_location_id.trim()
        ? data.auction_location_id.trim()
        : null,
    ...(wasNotSold && {
      dealQuality: dealQuality || undefined,
      grayBookWholeSalePrice: grayBookWholeSalePrice || undefined,
      grayBookRetailPrice: grayBookRetailPrice || undefined,
    }),
    ...(purchasedForInventoryDate !== undefined && { purchased_for_inventory_date: purchasedForInventoryDate }),
    ...(beingPaintedDate !== undefined && { being_painted_date: beingPaintedDate }),
    ...(titleTrackingUpdate && { title_tracking: titleTrackingUpdate }),
  }

  // Remove temporary fields that are now in pickup_location
  delete finalData.address
  delete finalData.city
  delete finalData.zip
  delete finalData.pickup_address
  delete finalData.pickup_city
  delete finalData.pickup_state
  delete finalData.pickup_zip
  delete finalData.pickup_contact_name
  delete finalData.pickup_contact_phone
  delete finalData.pickup_notes

  if (saveTimeoutRef.current) {
    clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = undefined
  }

  try {
    const result = await onSubmit(finalData)
    if (result === false) return

    clearFormData()
    if (isNew) {
      reset()
    }
  } finally {
    isSubmittingRef.current = false
    resumeSnapshotListener()
  }
}

  const handleClose = () => {
    if (resetConfirmationOpen) {
      return
    }
    onClose()
  }

  // Map vehicle status to actionType for SoldDialog
  const getActionTypeFromStatus = (status: string | null | undefined): 'sold' | 'sold_elsewhere' | 'pending_sale' | 'active_deal' | 'pending_available' | null => {
    if (!status) return null
    const statusLower = status.toLowerCase()
    if (statusLower === 'sold') return 'sold'
    if (statusLower === 'sold elsewhere') return 'sold_elsewhere'
    if (statusLower === 'pending sale') return 'pending_sale'
    if (statusLower === 'active deal') return 'active_deal'
    if (statusLower === 'pending available') return 'pending_available'
    return null
  }

  // Build initialData for SoldDialog from vehicle
  const buildInitialData = () => {
    if (!vehicle) return undefined

    const status = vehicle.status
    const isPendingSaleOrActiveDeal = status?.toLowerCase() === 'pending sale' || status?.toLowerCase() === 'active deal'
    const userForSoldTo = isPendingSaleOrActiveDeal ? vehicle.selling_to_user : vehicle.sold_to_user

    return {
      soldToUser: userForSoldTo || undefined,
      soldToContactId: isPendingSaleOrActiveDeal
        ? vehicle.selling_to_contact_id
        : vehicle.sold_to_contact_id,
      soldByUser: vehicle.sold_by_user || undefined,
      soldLocation: vehicle.sold_location || undefined,
      warranty: vehicle.warranty
        ? {
            warrantyTerms: vehicle.warranty.warranty_terms,
            warrantyCost: vehicle.warranty.warranty_cost,
            customerCost: vehicle.warranty.customer_cost,
            warrantyProfit: vehicle.warranty.warranty_profit,
          }
        : undefined,
      finance: vehicle.finance
        ? {
            paymentType: vehicle.finance.payment_type,
            downPayment: vehicle.finance.down_payment,
            bankName: vehicle.finance.bank_name,
            amountFinanced: vehicle.finance.amount_financed,
            points: vehicle.finance.points,
            profit: vehicle.finance.profit,
            totalSalePrice: vehicle.finance.total_sale_price,
            taxRate: vehicle.finance.tax_rate,
            includeTaxInSale: vehicle.finance.include_tax_in_sale,
            includeWarrantyInSale: vehicle.finance.include_warranty_in_sale,
            includeShippingInSale: vehicle.finance.include_shipping_in_sale,
            transleaseLease: Boolean(vehicle.translease_lease),
          }
        : {
            paymentType: 'cash' as const,
            downPayment: null,
            bankName: null,
            amountFinanced: null,
            points: null,
            profit: 0,
            totalSalePrice: 0,
            taxRate: null,
            includeTaxInSale: false,
            includeWarrantyInSale: false,
            includeShippingInSale: false,
            transleaseLease: Boolean(vehicle.translease_lease),
          },
      shipping: soldDialogShippingFromVehicle(vehicle.shipping),
      funded: Boolean(vehicle.funded) || Boolean(vehicle.funded_date),
      fundedDate: vehicle.funded_date ?? null,
    }
  }

  const handleNotifyPickupLocationNeeded = async (): Promise<boolean> => {
    if (!vehicle?.id) {
      showError('Vehicle ID is required')
      return false
    }

    if (isRefinance) {
      showInfo('Refinance trucks do not get a pickup address')
      return false
    }

    if (vehicle.ready_for_transport?.needs_address === true) {
      showInfo('Notification already sent')
      return true
    }

    try {
      setIsSendingPickupNotify(true)
      const currentReadyForTransport = vehicle.ready_for_transport || {
        needs_address: false,
        ready_for_pickup: false,
      }
      const readyForTransportUpdate: Record<string, unknown> = {
        needs_address: true,
        ready_for_pickup: currentReadyForTransport.ready_for_pickup || false,
      }
      if (currentReadyForTransport.customer !== undefined) {
        readyForTransportUpdate.customer = currentReadyForTransport.customer
      }
      if (currentReadyForTransport.repair !== undefined) {
        readyForTransportUpdate.repair = currentReadyForTransport.repair
      }
      if (currentReadyForTransport.note) {
        readyForTransportUpdate.note = currentReadyForTransport.note
      }
      if (currentReadyForTransport.ready_for_transport_date) {
        readyForTransportUpdate.ready_for_transport_date = currentReadyForTransport.ready_for_transport_date
      }

      await updateDocument({
        id: vehicle.id,
        data: {
          ready_for_transport: readyForTransportUpdate,
        },
      })
      showSuccess('Notification sent successfully')
      return true
    } catch (error) {
      console.error('Error sending notification:', error)
      showError(`Failed to send notification: ${error instanceof Error ? error.message : 'Unknown error'}`)
      return false
    } finally {
      setIsSendingPickupNotify(false)
    }
  }

  // Handle sold dialog submit - update vehicle without changing status
  const handleSoldDialogSubmit = async (
    soldToUser: { id: string; name: string },
    soldByUser: { id: string; name: string },
    sold_location: string,
    warrantyInfo: {
      warrantyTerms: string | null
      warrantyCost: number | null
      customerCost: number | null
      warrantyProfit: number | null
    } | null,
    financeInfo: {
      paymentType: string
      downPayment: number | null
      bankName: string | null
      amountFinanced: number | null
      points: number | null
      profit: number | null
      totalSalePrice: number | null
      taxRate: number | null
      includeTaxInSale: boolean
      includeWarrantyInSale: boolean
      includeShippingInSale: boolean
      transleaseLease: boolean
    } | null,
    shippingInfo?: {
      shipped: boolean | null
      ourShippingCost: number | null
      customerShippingCost: number | null
      profit: number | null
    } | null,
    funded?: boolean,
    soldToContactId?: string | null,
  ) => {
    if (!vehicle?.id) {
      showError('Vehicle ID is required')
      return false
    }

    const soldToUserData =
      soldToUser?.id && soldToUser?.name
        ? {
            id: String(soldToUser.id),
            name: String(soldToUser.name),
          }
        : { id: '', name: '' }

    const soldByUserData =
      soldByUser?.id && soldByUser?.name
        ? {
            id: String(soldByUser.id),
            name: String(soldByUser.name),
          }
        : { id: 'house', name: 'House' }

    const contactId = soldToContactId?.trim() || null
    const status = vehicle.status
    const isPendingSaleOrActiveDeal = status?.toLowerCase() === 'pending sale' || status?.toLowerCase() === 'active deal'

    // Build update data without changing status
    const updateData: any = {
      sold_by_user: soldByUserData,
      sold_location: sold_location,
    }

    // Set the appropriate user field based on status
    if (isPendingSaleOrActiveDeal) {
      updateData.selling_to_user = soldToUserData
      updateData.selling_to_contact_id = contactId
    } else {
      updateData.sold_to_user = soldToUserData
      updateData.sold_to_contact_id = contactId
    }

    // Add warranty information
    if (warrantyInfo) {
      updateData['warranty_purchaged'] = true
      updateData['warranty'] = {
        warranty_terms: warrantyInfo.warrantyTerms,
        warranty_cost: warrantyInfo.warrantyCost,
        customer_cost: warrantyInfo.customerCost,
        warranty_created_at: vehicle.warranty?.warranty_created_at || new Date().toISOString(),
        warranty_profit: warrantyInfo.warrantyProfit,
      }
    }

    // Add finance information
    if (financeInfo) {
      updateData['finance'] = {
        payment_type: financeInfo.paymentType,
        down_payment: financeInfo.downPayment !== null ? Number(financeInfo.downPayment) : null,
        bank_name: financeInfo.bankName,
        amount_financed: financeInfo.amountFinanced !== null ? Number(financeInfo.amountFinanced) : null,
        points: financeInfo.points !== null ? Number(financeInfo.points) : null,
        profit: financeInfo.profit !== null ? Number(financeInfo.profit) : null,
        total_sale_price: financeInfo.totalSalePrice !== null ? Number(financeInfo.totalSalePrice) : null,
        tax_rate: financeInfo.taxRate !== null ? Number(financeInfo.taxRate) : null,
        include_tax_in_sale: financeInfo.includeTaxInSale,
        include_warranty_in_sale: financeInfo.includeWarrantyInSale,
        include_shipping_in_sale: financeInfo.includeShippingInSale,
      }
      updateData['translease_lease'] = Boolean(financeInfo.transleaseLease)
    }

    const storedShipping = storedShippingFromDialog(shippingInfo)
    if (storedShipping) {
      updateData['shipping'] = storedShipping
    }

    // Add funded information (only for sold vehicles, not pending sale or active deal)
    if (funded !== undefined && !isPendingSaleOrActiveDeal) {
      updateData['funded'] = funded
      if (funded) {
        updateData['funded_date'] = vehicle.funded_date ?? new Date().toISOString()
      } else {
        // Clear funded_date when funded is set to false
        updateData['funded_date'] = null
      }
    }

    try {
      await updateDocument({
        id: vehicle.id,
        data: updateData,
      })
      showSuccess('Sale information updated successfully')
      setSoldDialogOpen(false)
      // Invalidate queries to refresh the vehicle data
      queryClient.invalidateQueries({ queryKey: [inventoryCollection] })
      return true
    } catch (error) {
      console.error('Error updating sale information:', error)
      showError(`Failed to update sale information: ${error instanceof Error ? error.message : 'Unknown error'}`)
      return false
    }
  }

  // Check if we should show the edit sale info button
  const shouldShowEditSaleButton = () => {
    if (!vehicle || isNew) return false
    const status = vehicle.status
    if (!status) return false
    const statusLower = status.toLowerCase()
    return (
      statusLower === 'sold' ||
      statusLower === 'pending sale' ||
      statusLower === 'active deal' ||
      statusLower === 'pending available'
    )
  }

  if (!vehicle && isNew && !userInfo) {
    return null
  }

  // Check if the vehicle is already sold
  const isVehicleSold = vehicle?.status?.toLowerCase() === 'sold'

  // Determine if the user can edit the sold date
  const canEditSoldDate = isAdmin || isOwner
  const sellingFinance = vehicle?.finance ?? null

  // Negotiation Guidance Component
  const NegotiationGuidance: React.FC<{
    sellerAskingPrice: number;
    wholesalePrice: number;
    dealQuality: Vehicle['dealQuality'];
  }> = ({ sellerAskingPrice, wholesalePrice, dealQuality }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const id = open ? 'deal-improvement-popper' : undefined;
    
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    };
    
    if (!sellerAskingPrice || !wholesalePrice || !dealQuality) return null;
    
    // Only calculate targets for good and great deals
    const goodDealPrice = getPriceForQuality('good', wholesalePrice, isOwner);
    const greatDealPrice = getPriceForQuality('great', wholesalePrice, isOwner);
    
    // Calculate negotiation amounts (how much to reduce asking price)
    const negotiationForGoodDeal = goodDealPrice ? sellerAskingPrice - goodDealPrice : 0;
    const negotiationForGreatDeal = greatDealPrice ? sellerAskingPrice - greatDealPrice : 0;
    
    // Skip if negotiation amounts aren't positive (meaning deal is already better than targets)
    const showGoodTarget = negotiationForGoodDeal > 0;
    const showGreatTarget = negotiationForGreatDeal > 0;
    
    // If nothing to show, don't render
    if (!showGoodTarget && !showGreatTarget) return null;
    
    // Get quality info objects for the different deal qualities
    const currentQualityInfo = getDealQualityInfo(dealQuality);
    const goodQualityInfo = getDealQualityInfo('good');
    const greatQualityInfo = getDealQualityInfo('great');
    
    // Quality chip component using the dealQualityInfo
    const QualityChip = ({ qualityInfo }: { qualityInfo: ReturnType<typeof getDealQualityInfo> }) => (
      <Chip 
        label={qualityInfo.label} 
        size="small"
        color={qualityInfo.color}
        sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
      />
    );
    
    return (
      <>
        <Tooltip title="Deal Improvement Guide" placement="top">
          <Box 
            aria-describedby={id}
            onClick={handleClick} 
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              ml: 0.5,
              borderRadius: '4px',
              border: '1px dashed',
              borderColor: 'action.disabled',
              p: '2px 6px',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'primary.main',
              }
            }}
          >
            <ShowChartIcon 
              fontSize="small" 
              color={open ? "primary" : "action"} 
              sx={{ mr: 0.5 }}
            />
            <Typography variant="caption" color={open ? "primary" : "text.secondary"} fontWeight="medium">
              Improve
            </Typography>
          </Box>
        </Tooltip>
        
        <Popper 
          id={id} 
          open={open} 
          anchorEl={anchorEl}
          placement="bottom-start"
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ]}
          sx={{ zIndex: 1300 }}
        >
          <Paper 
            elevation={3}
            sx={{ 
              p: 1.5,
              width: 300,
              borderRadius: 1,
              border: '1px solid rgba(0,0,0,0.1)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="medium">
                Deal Improvement Guide
              </Typography>
            </Box>
            
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              Negotiate to improve the current deal quality:
            </Typography>
            
            {/* Good Deal Target */}
            {showGoodTarget && (
              <Box sx={{ mb: showGreatTarget ? 2 : 0, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <QualityChip qualityInfo={goodQualityInfo} />
                    <Typography variant="body2" fontWeight="medium">
                      Target:
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: `${goodQualityInfo.color}.main`,
                      fontWeight: 'bold'
                    }}
                  >
                    ${goodDealPrice?.toLocaleString()}
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mt: 0.5,
                    bgcolor: theme => alpha(theme.palette[goodQualityInfo.color].light, 0.2),
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    Negotiate down:
                  </Typography>
                  <Typography 
                    variant="body1" 
                    fontWeight="bold" 
                    sx={{ color: `${goodQualityInfo.color}.dark` }}
                  >
                    ${negotiationForGoodDeal.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Great Deal Target */}
            {showGreatTarget && (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <QualityChip qualityInfo={greatQualityInfo} />
                    <Typography variant="body2" fontWeight="medium">
                      Target:
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: `${greatQualityInfo.color}.main`,
                      fontWeight: 'bold'
                    }}
                  >
                    ${greatDealPrice?.toLocaleString()}
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mt: 0.5,
                    bgcolor: theme => alpha(theme.palette[greatQualityInfo.color].light, 0.2),
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    Negotiate down:
                  </Typography>
                  <Typography 
                    variant="body1" 
                    fontWeight="bold" 
                    sx={{ color: `${greatQualityInfo.color}.dark` }}
                  >
                    ${negotiationForGreatDeal.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Popper>
      </>
    );
  };

  return (
    <>
      <Dialog
        fullScreen={isMobile}
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        sx={
          isMobile
            ? {
                '& .MuiDialog-paper': {
                  pt: SAFE_AREA_TOP,
                  pb: SAFE_AREA_BOTTOM,
                },
              }
            : undefined
        }
      >
        <DialogTitle
          p={0}
          justifyContent='center'
          alignContent='center'
          display='flex'
          sx={{ position: 'relative' }}
        >
          <Typography variant='h1'> {readOnly ? 'View' : (isNew ? 'Add' : 'Edit')} Truck</Typography>
        <Box 
            sx={{ 
              position: 'absolute', 
              right: isMobile ? 8 : 16, 
              top: '50%', 
              transform: 'translateY(-50%)', 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center', 
              gap: isMobile ? 0.5 : 1,
              width: isMobile ? '40%' : 'auto'
            }}
          >
            {/* Your responsive button */}
            {isNew ? <Button
              variant="contained"
              color='warning'
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
              onClick={formReset}
              sx={{ 
                minWidth: isMobile ? 'unset' : '80px',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              {isMobile ? 'RST' : 'Reset'}
            </Button>:<></>
            }
            
            {/* Existing info icon */}
            <Tooltip
              title="Real-time updates are paused. Changes by others won't be visible and may cause conflicts."
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'warning.light',
                    color: 'warning.contrastText',
                    '& .MuiTooltip-arrow': {
                      color: 'warning.light',
                    },
                  },
                },
              }}
            >
              <InfoIcon sx={{ color: 'warning.main', cursor: 'help' }} />
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            height: '100%',
            paddingBottom: 0,
            ...(isMobile ? { overflowX: 'hidden', px: 1.5 } : {}),
          }}
        >
          <Box
            component='form'
            noValidate
            onSubmit={handleSubmit(onSubmitForm)}
            height={{ xs: '100%', md: 'auto' }}
            width={{ xs: '100%', md: 'auto' }}
            p={0}
            overflow='hidden'
            sx={{
              ...(readOnly && {
                '& .MuiButton:not(.MuiButton-close)': {
                  pointerEvents: 'auto', // Allow buttons to be clicked in read-only mode
                },
                '& .MuiIconButton': {
                  pointerEvents: 'auto', // Allow icon buttons to be clicked in read-only mode
                }
              })
            }}
          >
            <Box 
              flex={1} 
              height={`calc(100% - ${isMobile ? '96px' : '64px'})`} 
              sx={{ 
                overflowY: 'auto',
                ...mobileFormScrollSx,
                ...(readOnly && {
                  px: 2,
                  py: 1,
                  '& .MuiGrid-item': {
                    mt: 1
                  }
                })
              }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant='h4' fontWeight='bold'>
                    Basic Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4} display='flex'>
                  <Box>
                    <Typography variant='subtitle2' gutterBottom>
                      Stock Number
                    </Typography>
                    <Typography variant='body1' sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                      {watch('stock_number') || ''}
                      {watch('status') && (
                        <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                          <Chip
                            size='small'
                            label={
                              watch('status')
                                ?.split(' ')
                                .map((word: string) => word.charAt(0))
                                .join('') || ''
                            }
                            sx={{
                              backgroundColor:
                                vehicleStatusColorMap[watch('status')]?.backgroundColor ||
                                vehicleStatusColorMap.default.backgroundColor,
                              color: `${vehicleStatusColorMap[watch('status')]?.color || 'default'}.main`,
                              fontWeight: 'medium',
                              minWidth: '24px',
                              height: '24px',
                              '& .MuiChip-label': {
                                padding: '0 4px',
                                textTransform: 'uppercase',
                              },
                            }}
                          />
                        </Box>
                      )}
                    </Typography>
                  </Box>
                </Grid>
                {isAdmin && (
                  <Grid item xs={12} md={8} display='flex' alignItems='center'>
                    {readOnly ? (
                      renderReadOnlyField(
                        'text',
                        'VIN',
                        watch('vin') || '-'
                      )
                    ) : (
                      <TextField
                        fullWidth
                        {...register('vin')}
                        label='VIN'
                        error={!!errors.vin || vinAlreadyExistsError}
                        helperText={
                          vinAlreadyExistsError ? 'Vehicle already exists in active inventory' : `${vin.length}/17`
                        }
                        defaultValue=''
                        InputLabelProps={{ shrink: !!watch('vin') }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              {vinAlreadyExistsError && (
                                <Tooltip title='This VIN already exists in active inventory' arrow>
                                  <InfoIcon color='warning' sx={{ fontSize: 20 }} />
                                </Tooltip>
                              )}
                              {isResell && (
                                <Tooltip
                                  title='This is a resell. The vehicle was previously sold. Click to populate vehicle information.'
                                  arrow
                                >
                                  <Button
                                    onClick={populateFromPreviousVehicle}
                                    sx={{ minWidth: 'auto', p: 0.5 }}
                                    color='primary'
                                    startIcon={<ReplayIcon />}
                                  >
                                    Resell
                                  </Button>
                                </Tooltip>
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </Grid>
                )}
                <Grid item xs={4} md={4}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'custom',
                      'Status',
                      watch('status'),
                      undefined,
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1">{watch('status') || '-'}</Typography>
                        {watch('status') && (
                          <Chip
                            size='small'
                            label={
                              watch('status')
                                ?.split(' ')
                                .map((word: string) => word.charAt(0))
                                .join('') || ''
                            }
                            sx={{
                              ml: 1,
                              backgroundColor:
                                vehicleStatusColorMap[watch('status')]?.backgroundColor ||
                                vehicleStatusColorMap.default.backgroundColor,
                              color: `${vehicleStatusColorMap[watch('status')]?.color || 'default'}.main`,
                              fontWeight: 'medium',
                              minWidth: '24px',
                              height: '24px',
                              '& .MuiChip-label': {
                                padding: '0 4px',
                                textTransform: 'uppercase',
                              },
                            }}
                          />
                        )}
                      </Box>
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.status}>
                      <InputLabel id='status-label'>Status</InputLabel>
                      <Controller
                        name='status'
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId='status-label'
                            label='Status'
                            required
                            value={field.value || ''}
                          >
                            <MenuItem value=''> </MenuItem>
                            {vehicleStatusOptions.map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      <FormHelperText>{errors.status?.message?.toString() || ''}</FormHelperText>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={8} md={8}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'custom',
                      'Web Link',
                      watch('link'),
                      undefined,
                      watch('link') ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ wordBreak: 'break-all', mr: 1 }}>
                            {watch('link')}
                          </Typography>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            onClick={() => window.open(watch('link'), '_blank')}
                            sx={{ ml: 'auto', minWidth: 'auto' }}
                          >
                            View
                          </Button>
                        </Box>
                      ) : '-'
                    )
                  ) : (
                    <TextField
                      fullWidth
                      {...register('link')}
                      label='Web Link'
                      error={!!errors.link}
                      helperText={errors.link?.message?.toString() || ''}
                      InputLabelProps={{ shrink: !!watch('link') }}
                      InputProps={{
                        endAdornment: watch('link') ? (
                          <InputAdornment position='end'>
                            <Button onClick={() => window.open(watch('link'), '_blank')}>View</Button>
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={4} md={watch('seller_type') === 'Auction' ? 3 : 4}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'select',
                      'Seller Type',
                      watch('seller_type'),
                      ['Private Party', 'Auction', 'Dealer', 'Repo']
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.seller_type}>
                      <InputLabel id='seller-type-label'>Seller Type</InputLabel>
                      <Controller
                        name='seller_type'
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId='seller-type-label'
                            label='Seller Type'
                            value={field.value || ''}
                            onChange={(e) => {
                              field.onChange(e)
                              if (e.target.value !== 'Auction') {
                                setValue('auction_location_id', '', {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                })
                              }
                            }}
                          >
                            <MenuItem value=''> </MenuItem>
                            <MenuItem value='Private Party'>Private Party</MenuItem>
                            <MenuItem value='Auction'>Auction</MenuItem>
                            <MenuItem value='Dealer'>Dealer</MenuItem>
                            <MenuItem value='Repo'>Repo</MenuItem>
                          </Select>
                        )}
                      />
                      <FormHelperText>{errors.seller_type?.message?.toString() || ''}</FormHelperText>
                    </FormControl>
                  )}
                </Grid>
                {watch('seller_type') === 'Auction' && (
                  <Grid item xs={4} md={3}>
                    {readOnly ? (
                      renderReadOnlyField(
                        'text',
                        'Auction',
                        getAuctionLabel(
                          auctionOptions.find((auction) => auction.id === watch('auction_location_id')) || {
                            id: watch('auction_location_id') || undefined,
                          },
                        ),
                      )
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <FormControl fullWidth error={!!errors.auction_location_id}>
                          <InputLabel id='auction-location-label'>Auction</InputLabel>
                          <Controller
                            name='auction_location_id'
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                labelId='auction-location-label'
                                label='Auction'
                                value={field.value || ''}
                                disabled={isLoadingAuctions}
                              >
                                <MenuItem value=''> </MenuItem>
                                {auctionOptions.map((auction) => (
                                  <MenuItem key={auction.id} value={auction.id}>
                                    {getAuctionLabel(auction)}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                          <FormHelperText>
                            {errors.auction_location_id?.message?.toString() ||
                              (auctionOptions.length === 0 ? 'No auctions yet — click + to add one' : '')}
                          </FormHelperText>
                        </FormControl>
                        <Tooltip title='Add auction'>
                          <IconButton
                            aria-label='Add auction'
                            onClick={() => setAuctionDialogOpen(true)}
                            sx={{ mt: 0.5 }}
                            color='primary'
                          >
                            <Add />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Grid>
                )}
                <Grid item xs={4} md={watch('seller_type') === 'Auction' ? 3 : 4}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'select',
                      'Title format',
                      watch('title_format') === 'electronic'
                        ? 'Electronic'
                        : watch('title_format') === 'paper'
                          ? 'Paper'
                          : watch('title_format'),
                      ['electronic', 'paper']
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.title_format} required={!!watch('purchased_for_inventory')}>
                      <InputLabel id='title-format-label'>Title format</InputLabel>
                      <Controller
                        name='title_format'
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId='title-format-label'
                            label='Title format'
                            required={!!watch('purchased_for_inventory')}
                            value={field.value || ''}
                          >
                            <MenuItem value=''> </MenuItem>
                            <MenuItem value='electronic'>Electronic</MenuItem>
                            <MenuItem value='paper'>Paper</MenuItem>
                          </Select>
                        )}
                      />
                      <FormHelperText>{errors.title_format?.message?.toString() || ''}</FormHelperText>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'checkbox',
                      'Exclude from title emails',
                      Boolean(watch('exclude_from_title_emails')),
                    )
                  ) : (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!watch('exclude_from_title_emails')}
                          onChange={(e) => {
                            setValue('exclude_from_title_emails', e.target.checked, {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            })
                          }}
                        />
                      }
                      label={
                        <Box>
                          <span>Exclude from title emails</span>
                          <Typography variant='caption' color='text.secondary' display='block'>
                            Leave this truck off the weekday 8 AM pending-sale title copy digest.
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                </Grid>
                {watch('seller_type') === 'Private Party' && (
                  <Grid item xs={12} md={6}>
                    {readOnly ? (
                      renderReadOnlyField(
                        'checkbox',
                        'Is there a lien?',
                        watch('has_lien') === true ? 'Yes' : watch('has_lien') === false ? 'No' : '-'
                      )
                    ) : (
                      <FormControl fullWidth error={!!errors.has_lien}>
                        <Typography variant='subtitle2' gutterBottom>
                          Is there a lien?
                        </Typography>
                        <Controller
                          name='has_lien'
                          control={control}
                          render={({ field }) => (
                            <RadioGroup
                              row
                              value={field.value === null || field.value === undefined ? '' : field.value ? 'yes' : 'no'}
                              onChange={(e) => {
                                const value = e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null
                                field.onChange(value)
                              }}
                            >
                              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
                              <FormControlLabel value='no' control={<Radio />} label='No' />
                            </RadioGroup>
                          )}
                        />
                        <FormHelperText>{errors.has_lien?.message?.toString() || ''}</FormHelperText>
                      </FormControl>
                    )}
                  </Grid>
                )}

                {isResell && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        backgroundColor: 'info.light',
                        color: 'info.contrastText',
                        borderRadius: 1,
                        px: 2,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <InfoIcon fontSize='small' />
                      <Typography variant='body2'>
                        This is a resell of a previously sold vehicle (Stock #{previousVehicle?.stock_number}). Click
                        the "Resell" button to auto-fill details from the previous listing.
                      </Typography>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} mt={2}>
                  <Typography variant='h4' fontWeight='bold'>
                    Truck Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'select',
                      'Make',
                      watch('make'),
                      makeOptions.concat(['Unknown'])
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.make}>
                      <InputLabel id='make-label'>Make</InputLabel>
                      <Controller
                        name='make'
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId='make-label'
                            label='Make'
                            required
                            onChange={handleMakeChange}
                            value={field.value || ''}
                          >
                            <MenuItem value=''> </MenuItem>
                            {makeOptions.map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                            <MenuItem value='Unknown'>Unknown</MenuItem>
                          </Select>
                        )}
                      />
                      <FormHelperText>{errors.make?.message?.toString() || ''}</FormHelperText>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'select',
                      'Model',
                      watch('model'),
                      getVehicleModelOptions(watch('make')).concat(['Unknown'])
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.model}>
                      <InputLabel id='model-label'>Model</InputLabel>
                      <Controller
                        name='model'
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId='model-label'
                            label='Model'
                            required
                            value={field.value || ''}
                          >
                            <MenuItem value=''> </MenuItem>
                            {getVehicleModelOptions(watch('make')).map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                            <MenuItem value='Unknown'>Unknown</MenuItem>
                          </Select>
                        )}
                      />
                      <FormHelperText>{errors.model?.message?.toString() || ''}</FormHelperText>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'select',
                      'Type',
                      watch('type'),
                      vehicleTypeOptions.concat(['Unknown'])
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.type}>
                      <InputLabel id='type-label'>Type</InputLabel>
                      <Controller
                        name='type'
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId='type-label'
                            label='Type'
                            required
                            onChange={handleTypeChange}
                            value={field.value || ''} 
                          >
                            <MenuItem value=''> </MenuItem>
                            {vehicleTypeOptions.map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                            <MenuItem value='Unknown'>Unknown</MenuItem>
                          </Select>
                        )}
                      />
                      <FormHelperText>{errors.type?.message?.toString() || ''}</FormHelperText>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'select',
                      'Size',
                      watch('size'),
                      getVehicleSizeOptions(watch('type'))
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.size}>
                      <InputLabel id='size-label'>Size</InputLabel>
                      <Controller
                        name='size'
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId='size-label'
                            label='Size'
                            required
                            ref={sizeSelectRef}
                            value={field.value || ''} 
                          >
                            <MenuItem value=''> </MenuItem>
                            {getVehicleSizeOptions(watch('type')).map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      <FormHelperText>{errors.size?.message?.toString() || ''}</FormHelperText>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'select',
                      'Fuel',
                      watch('fuel'),
                      vehicleFuelOptions
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.fuel}>
                      <InputLabel id='fuel-label'>Fuel</InputLabel>
                      <Controller
                        name='fuel'
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId='fuel-label'
                            label='Fuel'
                            required
                            value={field.value || ''}
                          >
                            <MenuItem value=''> </MenuItem>
                            {vehicleFuelOptions.map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      <FormHelperText>{errors.fuel?.message?.toString() || ''}</FormHelperText>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'text',
                      'Mileage',
                      watch('miles')?.toLocaleString() || '-'
                    )
                  ) : (
                    <TextField
                      fullWidth
                      {...register('miles', {
                        setValueAs: value => (value === '' ? undefined : parseInt(value, 10)),
                        valueAsNumber: true,
                      })}
                      label='Mileage'
                      error={!!errors.miles}
                      helperText={errors.miles?.message?.toString() || ''}
                      required
                      type='number'
                      inputProps={{ min: 0 }}
                      InputLabelProps={{ shrink: !!watch('miles') }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'text',
                      'Year',
                      watch('year') || '-'
                    )
                  ) : (
                    <TextField
                      fullWidth
                      {...register('year', {
                        setValueAs: value => (value === '' ? undefined : parseInt(value, 10)),
                        valueAsNumber: true,
                      })}
                      label='Year'
                      error={!!errors.year}
                      helperText={errors.year?.message?.toString() || ''}
                      required
                      type='number'
                      defaultValue={''}
                      inputProps={{ min: minYear, max: getMaxVehicleYear() }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'text',
                      'Location (State)',
                      watch('location') ? 
                        states.find(state => state.abbreviation === watch('location'))?.name || watch('location') 
                        : '-'
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.location}>
                      <Controller
                        name='location'
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            options={states}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') {
                                return option
                              }
                              // For selected value display, show only the abbreviation
                              return option.abbreviation || ''
                            }}
                            onChange={(_, newValue) => {
                              // Set the value to the abbreviation if an object is selected, otherwise use the string value
                              field.onChange(newValue ? newValue.abbreviation : '')
                            }}
                            renderInput={params => (
                              <TextField
                                {...params}
                                label='Location (State)'
                                error={!!errors.location}
                                helperText={errors.location?.message?.toString() || ''}
                                required
                              />
                            )}
                            isOptionEqualToValue={(option, value) => {
                              if (typeof value === 'string') {
                                return option.abbreviation === value || option.name === value
                              }
                              return option.abbreviation === value.abbreviation
                            }}
                            value={
                              field.value
                                ? states.find(state => state.abbreviation === field.value) || field.value
                                : null
                            }
                            renderOption={(props, option) => (
                              <Box component="li" {...props}>
                                <Typography variant="body2">
                                  {option.name} ({option.abbreviation})
                                </Typography>
                              </Box>
                            )}
                            filterOptions={(options, { inputValue }) => {
                              if (!inputValue) return options
                              
                              const searchTerm = inputValue.toLowerCase()
                              return options.filter((option) => {
                                const name = option.name.toLowerCase()
                                const abbreviation = option.abbreviation.toLowerCase()
                                return name.includes(searchTerm) || abbreviation.includes(searchTerm)
                              })
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'select',
                      'Condition',
                      watch('condition'),
                      ['Used', 'New']
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.condition}>
                      <InputLabel id='condition-label'>Condition</InputLabel>
                      <Controller
                        name='condition'
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId='condition-label'
                            label='Condition'
                            required
                            value={field.value || ''}
                          >
                            <MenuItem value=''> </MenuItem>
                            {['Used', 'New'].map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      <FormHelperText>{errors.condition?.message?.toString() || ''}</FormHelperText>
                    </FormControl>
                  )}
                </Grid>

                {/* Rear Wheel selection (only for Cutaway type) */}
                {watchedType === 'Cutaway' && (
                  <Grid item xs={12} md={6}>
                    {readOnly ? (
                      renderReadOnlyField(
                        'select',
                        'Rear Wheel Configuration',
                        watch('rear_wheel'),
                        ['SRW', 'DRW']
                      )
                    ) : (
                      <FormControl fullWidth error={!!errors.rear_wheel} required>
                        <InputLabel id="rear-wheel-label">Rear Wheel Configuration</InputLabel>
                        <Controller
                          name="rear_wheel"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              labelId="rear-wheel-label"
                              label="Rear Wheel Configuration"
                              value={field.value || ''}
                            >
                              <MenuItem value="SRW">Single Rear Wheel (SRW)</MenuItem>
                              <MenuItem value="DRW">Dual Rear Wheel (DRW)</MenuItem>
                            </Select>
                          )}
                        />
                        <FormHelperText>{errors.rear_wheel?.message?.toString() || 'Check the window sticker in Carfax'}</FormHelperText>
                      </FormControl>
                    )}
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'select',
                      'Color',
                      watch('color'),
                      ['White', 'Blue', 'Yellow', 'Being painted']
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.color}>
                      <InputLabel id='color-label'>Color</InputLabel>
                      <Controller
                        name='color'
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId='color-label'
                            label='Color'
                            value={field.value || 'White'}
                            onChange={e => {
                              const newValue = e.target.value as Vehicle['color']
                              field.onChange(newValue)
                              if (newValue === 'Being painted') {
                                setValue(
                                  'being_painted_date',
                                  vehicle?.being_painted_date || watch('being_painted_date') || new Date().toISOString(),
                                )
                              }
                            }}
                          >
                            <MenuItem value='White'>White</MenuItem>
                            <MenuItem value='Blue'>Blue</MenuItem>
                            <MenuItem value='Yellow'>Yellow</MenuItem>
                            <MenuItem value='Being painted'>Being painted</MenuItem>
                          </Select>
                        )}
                      />
                      <FormHelperText>{errors.color?.message?.toString() || ''}</FormHelperText>
                      {watch('color') === 'Being painted' && (() => {
                        const dateStr = watch('being_painted_date') || vehicle?.being_painted_date
                        if (!dateStr) return null
                        const start = new Date(dateStr).getTime()
                        if (Number.isNaN(start)) return null
                        const days = Math.floor((Date.now() - start) / (1000 * 60 * 60 * 24))
                        return (
                          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
                            Days since being painted: {days}
                          </Typography>
                        )
                      })()}
                    </FormControl>
                  )}
                </Grid>

                <Grid item xs={12} mt={2}>
                  <Typography variant='h5' fontWeight='bold'>
                    Equipment Details
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'radio',
                      'Has Cameras',
                      watch('cameras') || 'unknown',
                      [
                        { value: 'yes', label: 'Yes' },
                        { value: 'no', label: 'No' },
                        { value: 'unknown', label: 'Unknown' }
                      ]
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.cameras}>
                      <Typography variant='subtitle2' gutterBottom>
                        Has Cameras <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <Controller
                        name='cameras'
                        control={control}
                        render={({ field }) => (
                          <RadioGroup
                            {...field}
                            row
                            value={field.value || 'unknown'}
                            onChange={e => field.onChange(e.target.value)}
                          >
                            <FormControlLabel value='yes' control={<Radio />} label='Yes' />
                            <FormControlLabel value='no' control={<Radio />} label='No' />
                            <FormControlLabel value='unknown' control={<Radio />} label='Unknown' />
                          </RadioGroup>
                        )}
                      />
                      <FormHelperText>
                        {errors.cameras?.message?.toString() || ''}
                      </FormHelperText>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'radio',
                      'Has Shelves',
                      watch('shelves') || 'unknown',
                      [
                        { value: 'yes', label: 'Yes' },
                        { value: 'no', label: 'No' },
                        { value: 'unknown', label: 'Unknown' }
                      ]
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.shelves}>
                      <Typography variant='subtitle2' gutterBottom>
                        Has Shelves <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <Controller
                        name='shelves'
                        control={control}
                        render={({ field }) => (
                          <RadioGroup {...field} row value={field.value || 'unknown'}>
                            <FormControlLabel value='yes' control={<Radio />} label='Yes' />
                            <FormControlLabel value='no' control={<Radio />} label='No' />
                            <FormControlLabel value='unknown' control={<Radio />} label='Unknown' />
                          </RadioGroup>
                        )}
                      />
                      <FormHelperText>{errors.shelves?.message?.toString() || ''}</FormHelperText>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'radio',
                      'GVWR Over 10,000 lbs',
                      watch('gwvr_over_10k') || 'unknown',
                      [
                        { value: 'yes', label: 'Yes' },
                        { value: 'no', label: 'No' },
                        { value: 'unknown', label: 'Unknown' }
                      ]
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.gwvr_over_10k}>
                      <Typography variant='subtitle2' gutterBottom>
                        GVWR Over 10,000 lbs <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <Controller
                        name='gwvr_over_10k'
                        control={control}
                        render={({ field }) => (
                          <RadioGroup {...field} row value={field.value || 'unknown'}>
                            <FormControlLabel value='yes' control={<Radio />} label='Yes' />
                            <FormControlLabel value='no' control={<Radio />} label='No' />
                            <FormControlLabel value='unknown' control={<Radio />} label='Unknown' />
                          </RadioGroup>
                        )}
                      />
                      <FormHelperText>{errors.gwvr_over_10k?.message?.toString() || ''}</FormHelperText>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'checkbox',
                      'Length/size is confirmed',
                      Boolean(watch('size_confirmed'))
                    )
                  ) : (
                    <FormControl fullWidth error={!!errors.size_confirmed} sx={{ mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Controller
                            name='size_confirmed'
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                checked={field.value || false}
                                onChange={e => field.onChange(e.target.checked)}
                              />
                            )}
                          />
                        }
                        label='Length/size is confirmed'
                      />
                      <FormHelperText>{errors.size_confirmed?.message?.toString() || ''}</FormHelperText>
                    </FormControl>
                  )}
                </Grid>

                {isAdmin && (
                  <Grid item xs={12} mt={2}>
                    <Typography variant='h4' fontWeight='bold'>
                      Contact Information
                    </Typography>
                  </Grid>
                )}
                {!isBabyAdmin && (
                  <>
                    <Grid item xs={12} md={6}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'text',
                          'Who',
                          watch('who') || '-'
                        )
                      ) : (
                        <TextField
                          fullWidth
                          {...register('who')}
                          label='Who'
                          error={!!errors.who}
                          helperText={errors.who?.message?.toString() || ''}
                          InputLabelProps={{ shrink: !!watch('who') }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'text',
                          'Phone Number',
                          watch('phone_number') || '-'
                        )
                      ) : (
                        <TextField
                          fullWidth
                          {...register('phone_number')}
                          label='Phone Number'
                          placeholder='(123) 456-7890'
                          inputProps={{
                            maxLength: 14,
                          }}
                          onChange={e => handlePhoneNumberChange(e)}
                          error={!!errors.phone_number}
                          helperText={errors.phone_number?.message?.toString() || 'Format: (123) 456-7890'}
                          InputLabelProps={{ shrink: !!watch('phone_number') }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'text',
                          'Bought From',
                          watch('bought_from') || '-'
                        )
                      ) : (
                        <TextField
                          fullWidth
                          {...register('bought_from')}
                          label='Bought From'
                          error={!!errors.bought_from}
                          helperText={errors.bought_from?.message?.toString() || ''}
                          defaultValue=''
                          InputLabelProps={{
                            shrink: !!watch('bought_from'),
                          }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'checkbox',
                          'Purchased',
                          Boolean(watch('purchased_for_inventory'))
                        )
                      ) : (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!watch('purchased_for_inventory')}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Block purchase when pickup address is incomplete — prompt to notify owner
                                  // unless refinance (exempt) or the user may purchase without a pickup address
                                  if (
                                    pickupLocationState.isIncomplete &&
                                    !isRefinance &&
                                    !canPurchaseWithoutPickupAddress
                                  ) {
                                    setPurchaseNotifyDialogOpen(true)
                                    return
                                  }

                                  // Store current who value in bought_from
                                  const ownedByValue = watch('who') || '';

                                  // Use setValue with shouldDirty: true to properly update the form state
                                  setValue('bought_from', ownedByValue, {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true
                                  });

                                  // Set who to "Optic Truck Works"
                                  setValue('who', 'Optic Truck Works', {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true
                                  });
                                }

                                // Always update the purchased_for_inventory field based on the checkbox state
                                setValue('purchased_for_inventory', e.target.checked, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true
                                });
                              }}
                            />
                          }
                          label="Purchased"
                        />
                      )}
                    </Grid>
                  </>
                )}

                {/* Pickup Location section - moved directly under Contact Information */}
                {!isBabyAdmin && (
                  <>
                    <Grid item xs={12} mt={2}>
                      <Typography variant='h4' fontWeight='bold'>
                        Pickup Information
                      </Typography>
                    </Grid>
                    {isRefinance && (
                      <Grid item xs={12}>
                        <Alert severity='info'>
                          Refinance trucks do not get a pickup address. Pickup fields are disabled
                          and inventory is not notified for a missing address.
                        </Alert>
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'text',
                          'Pickup Contact Name',
                          watch('pickup_contact_name') || '-'
                        )
                      ) : (
                        <Autocomplete<PickupLocation | string, false, true, true>
                          options={savedPickupLocations || []}
                          disabled={isRefinance}
                          getOptionLabel={getPickupLocationSearchLabel}
                          filterOptions={(options, { inputValue }) =>
                            filterPickupLocationOptions(options, inputValue)
                          }
                          onChange={(_, newValue) => {
                            if (typeof newValue === 'string') {
                              setPickupContactInput(newValue)
                              setValue('pickup_contact_name', newValue, { shouldDirty: true })
                            } else if (newValue) {
                              handlePreviousAddressSelect(newValue)
                              const contactName = getPickupLocationContactLabel(newValue)
                              const fieldValue =
                                contactName === 'Unknown Contact' ? '' : contactName
                              setPickupContactInput(fieldValue)
                              setValue('pickup_contact_name', fieldValue, { shouldDirty: true })
                            } else {
                              setPickupContactInput('')
                              setValue('pickup_contact_name', '', { shouldDirty: true })
                            }
                          }}
                          onInputChange={(_, newInputValue, reason) => {
                            // Keep typing local — syncing RHF on every keystroke re-renders the
                            // whole vehicle form and freezes the dropdown (#1048).
                            if (reason === 'reset') return
                            setPickupContactInput(newInputValue)
                          }}
                          freeSolo
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Pickup Contact Name'
                              error={!!errors.pickup_contact_name}
                              helperText={
                                errors.pickup_contact_name?.message?.toString() ||
                                (isLoadingSavedLocations
                                  ? 'Loading saved locations...'
                                  : savedPickupLocations && savedPickupLocations.length > 0
                                    ? `Search by contact, company, or address (${savedPickupLocations.length} saved)`
                                    : 'Type to enter a new contact name')
                              }
                              InputLabelProps={{ shrink: !!pickupContactInput }}
                              onBlur={event => {
                                const inputOnBlur = params.inputProps.onBlur as
                                  | ((e: typeof event) => void)
                                  | undefined
                                inputOnBlur?.(event)
                                const value = event.target.value
                                setPickupContactInput(value)
                                setValue('pickup_contact_name', value, { shouldDirty: true })
                              }}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {isLoadingSavedLocations && <CircularProgress size={20} />}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {typeof option === 'string'
                                    ? option
                                    : getPickupLocationContactLabel(option)}
                                </Typography>
                                {typeof option !== 'string' && (
                                  <>
                                    {option.company_name?.trim() &&
                                      option.contact_name?.trim() && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {option.company_name.trim()}
                                        </Typography>
                                      )}
                                    <Typography variant="body2" color="text.secondary">
                                      {formatPickupLocationAddress(option) || 'No Address'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {option.contact_phone && `${option.contact_phone}`}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            </Box>
                          )}
                          isOptionEqualToValue={(option, value) => {
                            if (typeof option !== 'string' && typeof value !== 'string') {
                              if (option.id && value.id) return option.id === value.id
                            }
                            if (typeof value === 'string') {
                              return typeof option === 'string'
                                ? option === value
                                : getPickupLocationContactLabel(option) === value ||
                                    option.contact_name === value
                            }
                            return typeof option === 'string'
                              ? option === value.contact_name
                              : option.contact_name === value.contact_name
                          }}
                          inputValue={pickupContactInput}
                          loading={isLoadingSavedLocations}
                          noOptionsText={
                            isLoadingSavedLocations
                              ? 'Loading saved locations...'
                              : pickupContactInput.trim()
                                ? 'No saved locations match'
                                : 'Type to search contact, company, or address'
                          }
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'text',
                          'Pickup Contact Phone',
                          watch('pickup_contact_phone') || '-'
                        )
                      ) : (
                        <TextField
                          fullWidth
                          {...register('pickup_contact_phone')}
                          disabled={isRefinance}
                          label='Pickup Contact Phone'
                          placeholder='(123) 456-7890'
                          inputProps={{
                            maxLength: 14,
                          }}
                          onChange={e => {
                            const input = e.target.value.replace(/\D/g, '') // Remove non-digits
                            let formatted = ''

                            if (input.length <= 3) {
                              formatted = input
                            } else if (input.length <= 6) {
                              formatted = `(${input.slice(0, 3)}) ${input.slice(3)}`
                            } else {
                              formatted = `(${input.slice(0, 3)}) ${input.slice(3, 6)}-${input.slice(6, 10)}`
                            }

                            // Update the field value
                            setValue('pickup_contact_phone', formatted, { shouldValidate: true })
                          }}
                          error={!!errors.pickup_contact_phone}
                          helperText={errors.pickup_contact_phone?.message?.toString() || 'Format: (123) 456-7890'}
                          InputLabelProps={{ shrink: !!watch('pickup_contact_phone') }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={12}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'text',
                          'Pickup Address',
                          watch('pickup_address') || '-'
                        )
                      ) : (
                        <TextField
                          fullWidth
                          {...register('pickup_address')}
                          disabled={isRefinance}
                          label='Pickup Address'
                          error={!!errors.pickup_address}
                          helperText={errors.pickup_address?.message?.toString() || ''}
                          InputLabelProps={{ shrink: !!watch('pickup_address') }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'text',
                          'Pickup City',
                          watch('pickup_city') || '-'
                        )
                      ) : (
                        <TextField
                          fullWidth
                          {...register('pickup_city')}
                          disabled={isRefinance}
                          label='Pickup City'
                          error={!!errors.pickup_city}
                          helperText={errors.pickup_city?.message?.toString() || ''}
                          InputLabelProps={{ shrink: !!watch('pickup_city') }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'text',
                          'Pickup State',
                          (() => {
                            const stateValue = watch('pickup_state');
                            if (!stateValue) return '-';
                            const stateObj = states.find(state => state.abbreviation === stateValue);
                            return stateObj ? `${stateObj.name} (${stateObj.abbreviation})` : stateValue;
                          })()
                        )
                      ) : (
                        <FormControl fullWidth error={!!errors.pickup_state}>
                          <Controller
                            name='pickup_state'
                            control={control}
                            render={({ field }) => (
                              <Autocomplete
                                {...field}
                                disabled={isRefinance}
                                options={states}
                                getOptionLabel={(option) => {
                                  // Handle both string values and state objects
                                  if (typeof option === 'string') {
                                    // Find the state object by abbreviation or name
                                    const stateObj = states.find(
                                      state => state.abbreviation === option || state.name === option,
                                    )
                                    return stateObj ? `${stateObj.name} (${stateObj.abbreviation})` : option
                                  }
                                  return `${option.name} (${option.abbreviation})`
                                }}
                                onChange={(_, newValue) => {
                                  // Set the value to the abbreviation if an object is selected, otherwise use the string value
                                  field.onChange(newValue ? newValue.abbreviation : '')
                                }}
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    label='Pickup State'
                                    error={!!errors.pickup_state}
                                    helperText={errors.pickup_state?.message?.toString() || ''}
                                  />
                                )}
                                isOptionEqualToValue={(option, value) => {
                                  if (typeof value === 'string') {
                                    return option.abbreviation === value || option.name === value
                                  }
                                  return option.abbreviation === value.abbreviation
                                }}
                                value={
                                  field.value
                                    ? states.find(state => state.abbreviation === field.value) || field.value
                                    : null
                                }
                              />
                            )}
                          />
                        </FormControl>
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'text',
                          'Pickup Zip Code',
                          watch('pickup_zip') || '-'
                        )
                      ) : (
                        <TextField
                          fullWidth
                          {...register('pickup_zip')}
                          disabled={isRefinance}
                          label='Pickup Zip Code'
                          error={!!errors.pickup_zip}
                          helperText={errors.pickup_zip?.message?.toString() || ''}
                          InputLabelProps={{ shrink: !!watch('pickup_zip') }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'custom',
                          'Pickup Notes',
                          watch('pickup_notes'),
                          undefined,
                          <Box>
                            <Box sx={{ whiteSpace: 'pre-wrap' }}>
                              {watch('pickup_notes') || '-'}
                            </Box>
                            <Typography
                              variant='body2'
                              color='error.main'
                              sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}
                            >
                              This note will be saved in location management
                            </Typography>
                          </Box>
                        )
                      ) : (
                        <Box>
                          <TextField
                            fullWidth
                            {...register('pickup_notes')}
                            disabled={isRefinance}
                            label='Pickup Notes'
                            multiline
                            rows={2}
                            error={!!errors.pickup_notes}
                            helperText={errors.pickup_notes?.message?.toString() || ''}
                            InputLabelProps={{ shrink: !!watch('pickup_notes') }}
                          />
                          <Typography
                            variant='body2'
                            color='error.main'
                            sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}
                          >
                            This note will be saved in location management
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'text',
                          'Refinance',
                          watch('refinance') ? 'Yes' : 'No'
                        )
                      ) : (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(watch('refinance'))}
                              onChange={(e) =>
                                setValue('refinance', e.target.checked, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true,
                                })
                              }
                            />
                          }
                          label='Refinance'
                        />
                      )}
                    </Grid>
                    {!readOnly && !isRefinance && canSaveLocation() && !locationExists() && (
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          startIcon={<Save />}
                          onClick={handleOpenSaveLocation}
                          fullWidth
                          sx={{ mt: 1 }}
                        >
                          Save This Location
                        </Button>
                      </Grid>
                    )}
                    {!readOnly && (
                      <>
                        {canMarkForTransport &&
                          !isRefinance &&
                          pickupLocationState.isAddressComplete &&
                          !!vehicle?.id &&
                          !vehicle?.ready_for_transport?.ready_for_pickup && (
                            <Grid item xs={12}>
                              <Button
                                variant="contained"
                                color="success"
                                fullWidth
                                onClick={async () => {
                                  if (!vehicle?.id) {
                                    showError('Vehicle ID is required')
                                    return
                                  }

                                  try {
                                    const currentReadyForTransport = vehicle.ready_for_transport || {
                                      needs_address: false,
                                      ready_for_pickup: false,
                                    }

                                    await updateDocument({
                                      id: vehicle.id,
                                      data: {
                                        ready_for_transport: {
                                          ...currentReadyForTransport,
                                          needs_address: false,
                                          ready_for_pickup: true,
                                          ready_for_transport_date:
                                            currentReadyForTransport.ready_for_transport_date || new Date().toISOString(),
                                        },
                                      },
                                    })

                                    showSuccess('Truck marked ready for transport')
                                  } catch (error) {
                                    console.error('Error marking truck ready for transport:', error)
                                    showError(
                                      `Failed to mark ready for transport: ${error instanceof Error ? error.message : 'Unknown error'}`,
                                    )
                                  }
                                }}
                              >
                                Mark ready for transport
                              </Button>
                            </Grid>
                          )}
                        {vehicle?.ready_for_transport?.ready_for_pickup && (
                          <Grid item xs={12}>
                            <Chip
                              icon={<CheckCircle />}
                              label="Ready for transport"
                              color="success"
                              sx={{ fontWeight: 600 }}
                            />
                            {canMarkForTransport && (
                              <Button
                                variant="outlined"
                                color="warning"
                                fullWidth
                                sx={{ mt: 1 }}
                                onClick={async () => {
                                  if (!vehicle?.id) {
                                    showError('Vehicle ID is required')
                                    return
                                  }

                                  try {
                                    await updateDocumentAsync({
                                      id: vehicle.id,
                                      data: buildUnmarkReadyForPickupUpdate(vehicle),
                                    })
                                    showSuccess('Unmarked ready for pickup')
                                  } catch (error) {
                                    console.error('Error unmarking ready for pickup:', error)
                                    showError(
                                      `Failed to unmark ready for pickup: ${error instanceof Error ? error.message : 'Unknown error'}`,
                                    )
                                  }
                                }}
                              >
                                Unmark ready for pickup
                              </Button>
                            )}
                          </Grid>
                        )}
                        {canMarkForTransport && !isRefinance && pickupLocationState.isIncomplete && (
                            <Grid item xs={12}>
                              <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                                disabled={!vehicle?.id || vehicle?.ready_for_transport?.needs_address === true || isSendingPickupNotify}
                                onClick={async () => {
                                  await handleNotifyPickupLocationNeeded()
                                }}
                              >
                                {vehicle?.ready_for_transport?.needs_address === true ? 'Notification already sent' : 'Notify inventory'}
                              </Button>
                            </Grid>
                          )}
                        </>
                      )}
                  </>
                )}

                {/* Gate Passes Section */}
                {!isBabyAdmin && !isNew && vehicle && (
                  <>
                    <Grid item xs={12} mt={2}>
                      <Typography variant='h4' fontWeight='bold'>
                        Gate Passes
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Upload Files
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Upload, remove, or replace gate pass documents for this vehicle
                              </Typography>
                            </Box>
                            {!readOnly && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Add />}
                                onClick={triggerGatePassFileInput}
                                disabled={isUploadingGatePass || isDeletingGatePass}
                                sx={{ textTransform: 'none' }}
                              >
                                Add Files
                              </Button>
                            )}
                            <input
                              ref={gatePassFileInputRef}
                              type="file"
                              multiple
                              style={{ display: 'none' }}
                              onChange={handleGatePassFileInputChange}
                            />
                          </Box>

                          {/* Upload Progress */}
                          {isUploadingGatePass && (
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                                  Uploading files...
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {uploadProgress}%
                                </Typography>
                              </Box>
                              <LinearProgress variant="determinate" value={uploadProgress} />
                            </Box>
                          )}

                          <Divider sx={{ mb: 2 }} />

                          {/* Files List */}
                          <Box>
                            {!vehicle.gate_passes || vehicle.gate_passes.length === 0 ? (
                              <Box sx={{ textAlign: 'center', py: 3 }}>
                                <AttachFile sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                  No gate pass files uploaded
                                </Typography>
                              </Box>
                            ) : (
                              vehicle.gate_passes.map((file, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 2,
                                    mb: 1,
                                    backgroundColor: 'grey.50',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    '&:hover': {
                                      backgroundColor: 'grey.100',
                                    },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                    <Box sx={{ mr: 2, fontSize: 24 }}>
                                      {getFileIcon(file.fileType)}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 500,
                                          mb: 0.5,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {file.fileName}
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Typography variant="caption" color="text.secondary">
                                          {formatFileSize(file.fileSize)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {file.fileType || 'Unknown type'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Uploaded: {formatUploadDate(file.uploadedAt)}
                                        </Typography>
                                        {file.uploadedBy && (
                                          <Typography variant="caption" color="text.secondary">
                                            By: {file.uploadedBy}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                                    <Tooltip title="Download file">
                                      <IconButton
                                        size="small"
                                        component="a"
                                        href={file.url}
                                        download={file.fileName}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                          color: 'primary.main',
                                          '&:hover': {
                                            backgroundColor: 'primary.50',
                                          },
                                        }}
                                      >
                                        <Download fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Open in new tab">
                                      <IconButton
                                        size="small"
                                        component="a"
                                        href={file.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                          color: 'primary.main',
                                          '&:hover': {
                                            backgroundColor: 'primary.50',
                                          },
                                        }}
                                      >
                                        <OpenInNew fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {!readOnly && (
                                      <Tooltip title="Remove gate pass">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            disabled={isUploadingGatePass || isDeletingGatePass}
                                            onClick={() => setGatePassPendingDelete(file)}
                                            sx={{
                                              '&:hover': {
                                                backgroundColor: 'error.50',
                                              },
                                            }}
                                          >
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Box>
                              ))
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                )}

                {isAdmin && (
                  <Grid item xs={12} mt={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant='h4' fontWeight='bold'>
                        Finance Information
                      </Typography>
                      {shouldShowEditSaleButton() && !readOnly && (
                        <Button
                          variant="outlined"
                          startIcon={<Edit />}
                          onClick={() => setSoldDialogOpen(true)}
                          size="small"
                        >
                          Edit {vehicle?.status} information
                        </Button>
                      )}
                    </Box>
                  </Grid>
                )}
                {!isBabyAdmin && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant='h6' fontWeight='bold'>
                        Buying Finance
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'money',
                          'Optic List Price',
                          watch('optic_list_price')
                        )
                      ) : (
                        <TextField
                          fullWidth
                          {...register('optic_list_price', {
                            setValueAs: value => (value === '' ? null : parseFloat(value)),
                            valueAsNumber: true,
                          })}
                          label='Optic List Price'
                          error={!!errors.optic_list_price}
                          helperText={errors.optic_list_price?.message?.toString() || ''}
                          type='number'
                          defaultValue={null}
                          inputProps={{ min: 0, step: '0.01' }}
                          InputProps={{
                            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                          }}
                        />
                      )}
                    </Grid>
                    {watch('has_lien') === true && (
                      <>
                        <Grid item xs={12} md={6}>
                          {readOnly ? (
                            renderReadOnlyField(
                              'text',
                              'Lender',
                              watch('lien_lender') || '-'
                            )
                          ) : (
                            <TextField
                              fullWidth
                              {...register('lien_lender')}
                              label='Lender'
                              error={!!errors.lien_lender}
                              helperText={errors.lien_lender?.message?.toString() || ''}
                              InputLabelProps={{ shrink: !!watch('lien_lender') }}
                            />
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          {readOnly ? (
                            renderReadOnlyField(
                              'money',
                              'Payoff Amount',
                              watch('lien_payoff_amount')
                            )
                          ) : (
                            <TextField
                              fullWidth
                              {...register('lien_payoff_amount', {
                                setValueAs: value => (value === '' ? null : parseFloat(value)),
                                valueAsNumber: true,
                              })}
                              label='Payoff Amount'
                              error={!!errors.lien_payoff_amount}
                              helperText={errors.lien_payoff_amount?.message?.toString() || ''}
                              type='number'
                              defaultValue={null}
                              inputProps={{ min: 0, step: '0.01' }}
                              InputProps={{
                                startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                              }}
                            />
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          {readOnly ? (
                            renderReadOnlyField(
                              'text',
                              'Good Through Date',
                              watch('lien_good_through_date') ? 
                                new Date(watch('lien_good_through_date')).toLocaleDateString() : 
                                '-'
                            )
                          ) : (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <Controller
                                name='lien_good_through_date'
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <DatePicker
                                    label='Good Through Date'
                                    value={value ? dayjs(value) : null}
                                    onChange={date => onChange(date ? date.toISOString() : null)}
                                    slotProps={{
                                      textField: {
                                        fullWidth: true,
                                        helperText: errors.lien_good_through_date?.message?.toString() || '',
                                        error: !!errors.lien_good_through_date,
                                      },
                                    }}
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          )}
                        </Grid>
                        {watch('seller_type') === 'Private Party' && (
                          <Grid item xs={12} md={6}>
                            {readOnly ? (
                              renderReadOnlyField(
                                'text',
                                'Account Number',
                                watch('lien_account_number') || '-'
                              )
                            ) : (
                              <TextField
                                fullWidth
                                {...register('lien_account_number')}
                                label='Account Number'
                                error={!!errors.lien_account_number}
                                helperText={errors.lien_account_number?.message?.toString() || ''}
                                InputLabelProps={{ shrink: !!watch('lien_account_number') }}
                              />
                            )}
                          </Grid>
                        )}
                        <Grid item xs={12} md={6}>
                          {(() => {
                            const sellerAskingPrice = watch('seller_asking_price') || 0
                            const payoffAmount = watch('lien_payoff_amount') || 0
                            const equity = sellerAskingPrice - payoffAmount
                            
                            return readOnly ? (
                              renderReadOnlyField(
                                'money',
                                'Equity',
                                equity
                              )
                            ) : (
                              <TextField
                                fullWidth
                                label='Equity'
                                value={equity.toLocaleString('en-US', { 
                                  style: 'currency', 
                                  currency: 'USD',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                                InputProps={{
                                  readOnly: true,
                                }}
                                InputLabelProps={{ shrink: true }}
                              />
                            )
                          })()}
                        </Grid>
                      </>
                    )}
                    {canViewPurchasePrice && (
                      <Grid item xs={12} md={6}>
                        {readOnly ? (
                          renderReadOnlyField(
                            'money',
                            'Seller Asking Price',
                            watch('seller_asking_price')
                          )
                        ) : (
                          <TextField
                            fullWidth
                            {...register('seller_asking_price', {
                              setValueAs: value => (value === '' ? null : parseFloat(value)),
                              valueAsNumber: true,
                            })}
                            label='Seller Asking Price'
                            error={!!errors.seller_asking_price}
                            helperText={errors.seller_asking_price?.message?.toString() || ''}
                            required
                            type='number'
                            defaultValue={null}
                            inputProps={{ min: 0, step: '0.01' }}
                            InputProps={{
                              startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                            }}
                          />
                        )}
                      </Grid>
                    )}
                    {canViewProfit && matchedFormula && grayBookWholeSalePrice && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant='body1' sx={{ color: 'text.primary' }}>
                            Gray Book Price:{' '}
                            <span style={{ fontWeight: 500 }}>${grayBookWholeSalePrice.toLocaleString()}</span>
                          </Typography>
                          {userInfo?.isOwner && grayBookRetailPrice && (
                            <Typography variant='body1' sx={{ color: 'black' }}>
                              (Retail:{' '}
                              <span style={{ fontWeight: 500 }}>${grayBookRetailPrice.toLocaleString()}</span>)
                            </Typography>
                          )}
                        </Box>

                        {dealQualityInfo && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant='body1'>Deal Quality:</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip label={dealQualityInfo.label} color={dealQualityInfo.color} size='small' />
                              {grayBookWholeSalePrice && watch('seller_asking_price') && dealQuality && (
                                <NegotiationGuidance
                                  sellerAskingPrice={Number(watch('seller_asking_price'))}
                                  wholesalePrice={grayBookWholeSalePrice || 0}
                                  dealQuality={dealQuality}
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                      </Grid>
                    )}
                  </>
                )}
                {canMarkSold && (
                  <>
                    <Grid item xs={12} mt={2}>
                      <Typography variant='h6' fontWeight='bold'>
                        Selling Finance
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderReadOnlyField('text', 'Payment Type', sellingFinance?.payment_type || '-')}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderReadOnlyField('money', 'Down Payment', sellingFinance?.down_payment)}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderReadOnlyField('text', 'Bank Name', sellingFinance?.bank_name || '-')}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderReadOnlyField('money', 'Amount Financed', sellingFinance?.amount_financed)}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderReadOnlyField(
                        'text',
                        'Points',
                        sellingFinance?.points !== null && sellingFinance?.points !== undefined
                          ? `${sellingFinance.points}%`
                          : '-'
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderReadOnlyField('money', 'Profit', sellingFinance?.profit)}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderReadOnlyField('money', 'Total Sale Price', sellingFinance?.total_sale_price)}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderReadOnlyField(
                        'text',
                        'Tax Rate',
                        sellingFinance?.tax_rate !== null && sellingFinance?.tax_rate !== undefined
                          ? `${sellingFinance.tax_rate}%`
                          : '-'
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {renderReadOnlyField(
                        'text',
                        'Include Tax in Sale',
                        sellingFinance?.include_tax_in_sale ? 'Yes' : 'No'
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {renderReadOnlyField(
                        'text',
                        'Include Warranty in Sale',
                        sellingFinance?.include_warranty_in_sale ? 'Yes' : 'No'
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {renderReadOnlyField(
                        'text',
                        'Include Shipping in Sale',
                        sellingFinance?.include_shipping_in_sale ? 'Yes' : 'No'
                      )}
                    </Grid>
                  </>
                )}
                {canEditSoldDate && (
                  <Grid item xs={12} md={6}>
                    {readOnly ? (
                      renderReadOnlyField(
                        'text',
                        'Sold Date',
                        watch('sold_date') ? 
                          new Date(watch('sold_date')).toLocaleDateString() : 
                          '-'
                      )
                    ) : (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Controller
                          name='sold_date'
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <DatePicker
                              disabled={!canEditSoldDate}
                              label='Sold Date'
                              value={value ? dayjs(value) : null}
                              onChange={date => onChange(date ? date.toISOString() : null)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  helperText: errors.sold_date?.message?.toString() || '',
                                  error: !!errors.sold_date,
                                },
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    )}
                  </Grid>
                )}
                {!isBabyAdmin && (
                  <>
                    <Grid item xs={12} mt={2}>
                      <Typography variant='h6' fontWeight='bold'>
                        Funding
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      {readOnly ? (
                        renderReadOnlyField(
                          'checkbox',
                          'TransLease - Lease',
                          Boolean(watch('translease_lease'))
                        )
                      ) : (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!watch('translease_lease')}
                              disabled={sellingFinance?.payment_type === 'cash'}
                              onChange={(e) => {
                                setValue('translease_lease', e.target.checked, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true,
                                })
                              }}
                            />
                          }
                          label='TransLease - Lease'
                          disabled={sellingFinance?.payment_type === 'cash'}
                        />
                      )}
                    </Grid>
                  </>
                )}
                {!isBabyAdmin && (
                  <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    {readOnly ? (
                      <>
                        {renderReadOnlyField(
                          'checkbox',
                          'Paid for',
                          Boolean(watch('paid_for'))
                        )}
                        {renderReadOnlyField(
                          'checkbox',
                          'Funded',
                          Boolean(watch('funded'))
                        )}
                        {renderReadOnlyField(
                          'checkbox',
                          'Delivered to customer',
                          Boolean(watch('delivered_to_customer'))
                        )}
                      </>
                    ) : (
                      <>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!watch('paid_for')}
                              onChange={(e) => {
                                const isChecked = e.target.checked
                                setValue('paid_for', isChecked, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true
                                })
                                // Set paid_for_date when paid_for is checked
                                if (isChecked) {
                                  setValue('paid_for_date', new Date().toISOString(), {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true
                                  })
                                } else {
                                  // Clear paid_for_date when unchecked
                                  setValue('paid_for_date', null, {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true
                                  })
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Paid for</span>
                              {watch('paid_for') && watch('paid_for_date') && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  ({new Date(watch('paid_for_date')).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })})
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!watch('funded')}
                              onChange={(e) => {
                                const isChecked = e.target.checked
                                setValue('funded', isChecked, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true
                                })
                                // Set funded_date when funded is checked
                                if (isChecked) {
                                  setValue('funded_date', new Date().toISOString(), {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true
                                  })
                                } else {
                                  // Clear funded_date when unchecked
                                  setValue('funded_date', null, {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true
                                  })
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Funded</span>
                              {watch('funded') && watch('funded_date') && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  ({new Date(watch('funded_date')).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })})
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!watch('delivered_to_customer')}
                              onChange={(e) => {
                                setValue('delivered_to_customer', e.target.checked, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true
                                })
                              }}
                            />
                          }
                          label="Delivered to customer"
                        />
                      </>
                    )}
                  </Grid>
                )}

                <Grid item xs={12} mt={2}>
                  <Typography variant='h4' fontWeight='bold'>
                    Extra Information
                  </Typography>
                </Grid>

                {/* Date Available Field */}
                {watch('status') === 'Pending Available' && (
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={watch('date_available') === 'TBD'}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setValue('date_available', 'TBD')
                            } else {
                              setValue('date_available', null)
                            }
                          }}
                        />
                      }
                      label="To Be Determined"
                      sx={{ mb: 2 }}
                    />
                    
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Controller
                        name='date_available'
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <DatePicker
                            label='Date Available'
                            value={value && value !== 'TBD' ? dayjs(value) : null}
                            onChange={date => onChange(date ? date.toISOString() : null)}
                            disabled={value === 'TBD'}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                helperText: value === 'TBD' ? 'Date selection disabled when TBD is selected' : (errors.date_available?.message?.toString() || ''),
                                error: !!errors.date_available,
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                )}

                <Grid item xs={12}>
                  {readOnly ? (
                    renderReadOnlyField(
                      'custom',
                      'Notes',
                      watch('truck_notes'),
                      undefined,
                      <Box sx={{ whiteSpace: 'pre-wrap' }}>
                        {watch('truck_notes') || '-'}
                      </Box>
                    )
                  ) : (
                    <TextField
                      fullWidth
                      {...register('truck_notes')}
                      label='Notes'
                      multiline
                      rows={4}
                      error={!!errors.truck_notes}
                      helperText={errors.truck_notes?.message?.toString() || ''}
                      InputLabelProps={{ shrink: !!watch('truck_notes') }}
                    />
                  )}
                </Grid>

                <Grid item xs={12}>
                  {estimatedRepairAddonOptions.length > 0 && (
                    <Paper
                      variant='outlined'
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 1.5,
                        bgcolor: theme => alpha(theme.palette.grey[500], 0.04),
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant='subtitle2' fontWeight={600}>
                        Additional expense items
                      </Typography>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ display: 'block', mb: 1.5 }}
                      >
                        {readOnly
                          ? 'Items selected for this vehicle, included in the estimate below.'
                          : 'Check what this vehicle needs. Amounts are added to the category below.'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {estimatedRepairAddonOptions.map(item => {
                          const applied = watchedEstimatedRepairAddons.find(
                            addon => addon.id === item.id,
                          )
                          const isChecked = Boolean(applied)
                          const amount = applied?.amount ?? item.amount
                          const category = applied?.category ?? item.category
                          const lineTotal = applied
                            ? repairAddonLineTotal(applied)
                            : Number(amount) || 0
                          const amountLabel = `$${Number(lineTotal).toLocaleString('en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}`

                          return (
                            <FormControlLabel
                              key={item.id}
                              disabled={readOnly}
                              control={
                                <Checkbox
                                  size='small'
                                  checked={isChecked}
                                  onChange={e =>
                                    toggleEstimatedRepairAddon(item, e.target.checked)
                                  }
                                />
                              }
                              sx={{
                                m: 0,
                                pl: 0.5,
                                pr: 1.5,
                                py: 0.75,
                                width: '100%',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: isChecked ? 'primary.main' : 'divider',
                                bgcolor: theme =>
                                  isChecked
                                    ? alpha(theme.palette.primary.main, 0.06)
                                    : 'background.paper',
                                transition: 'border-color 120ms ease, background-color 120ms ease',
                                '&:hover': readOnly
                                  ? undefined
                                  : { borderColor: 'primary.light' },
                                '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
                                '&.Mui-disabled': { opacity: 1 },
                              }}
                              label={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      variant='body2'
                                      fontWeight={500}
                                      color='text.primary'
                                      noWrap
                                    >
                                      {item.name}
                                    </Typography>
                                    {item.isRemovedFromCatalog && (
                                      <Typography variant='caption' color='text.secondary'>
                                        Removed from expense settings
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip
                                    size='small'
                                    variant='outlined'
                                    label={estimatedCategoryLabel(category)}
                                    color={isChecked ? 'primary' : 'default'}
                                  />
                                  {isChecked ? (
                                    <RepairAddonQuantityStepper
                                      value={applied?.quantity}
                                      disabled={readOnly}
                                      onChange={qty =>
                                        changeEstimatedRepairAddonQuantity(item.id, qty)
                                      }
                                    />
                                  ) : null}
                                  <Typography
                                    variant='body2'
                                    fontWeight={600}
                                    sx={{
                                      minWidth: 72,
                                      textAlign: 'right',
                                      color: isChecked ? 'primary.main' : 'text.secondary',
                                    }}
                                  >
                                    {amountLabel}
                                  </Typography>
                                </Box>
                              }
                            />
                          )
                        })}
                      </Box>
                    </Paper>
                  )}

                  <Paper
                    variant='outlined'
                    sx={{
                      p: 2,
                      borderRadius: 1.5,
                      bgcolor: theme => alpha(theme.palette.grey[500], 0.04),
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1.5 }}>
                      Estimated repair cost
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {(
                        [
                          {
                            name: 'estimated_body' as const,
                            label: 'Body',
                            error: errors.estimated_body,
                          },
                          {
                            name: 'estimated_mechanical' as const,
                            label: 'Mechanical',
                            error: errors.estimated_mechanical,
                          },
                          {
                            name: 'estimated_safety' as const,
                            label: 'Safety',
                            error: errors.estimated_safety,
                          },
                        ] as const
                      ).map(field => (
                        <Box key={field.name}>
                          {readOnly ? (
                            <Box>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                sx={{ display: 'block', mb: 0.5 }}
                              >
                                {field.label}
                              </Typography>
                              <Typography variant='body1' fontWeight={500}>
                                {watch(field.name) != null &&
                                !Number.isNaN(Number(watch(field.name)))
                                  ? `$${Number(watch(field.name)).toLocaleString('en-US', {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 2,
                                    })}`
                                  : '—'}
                              </Typography>
                            </Box>
                          ) : (
                            <TextField
                              fullWidth
                              size='small'
                              {...register(field.name, {
                                setValueAs: value => (value === '' ? null : parseFloat(value)),
                                valueAsNumber: true,
                              })}
                              label={field.label}
                              error={!!field.error}
                              helperText={field.error?.message?.toString() || ''}
                              type='number'
                              inputProps={{ min: 0, step: '0.01' }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position='start'>$</InputAdornment>
                                ),
                              }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        px: 0.5,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                      }}
                    >
                      <Typography variant='body2' color='text.secondary' fontWeight={500} sx={{ pl: 0.5 }}>
                        Total
                      </Typography>
                      <Typography
                        variant='h6'
                        fontWeight={700}
                        sx={{
                          pr: 1,
                          color:
                            estimatedRepairCostTotal != null ? 'primary.main' : 'text.disabled',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {estimatedRepairCostTotal != null
                          ? `$${estimatedRepairCostTotal.toLocaleString('en-US', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                            })}`
                          : '—'}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                {vehicle?.repair_cost_over_budget_reason && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'warning.light',
                        bgcolor: 'warning.50',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Repair budget exceeded — reason
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {vehicle.repair_cost_over_budget_reason}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Photo Display Section - Only show when editing existing vehicle */}
                {!isNew && vehicle && (
                  <>
                    <Grid item xs={12} mt={2}>
                      <Typography variant='h4' fontWeight='bold'>
                        Vehicle Photos
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <VehiclePhotosSection 
                        collectionName={inventoryCollection}
                        vehicleId={vehicle.id}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
            <Box
              display='flex'
              flexDirection={isMobile ? 'column-reverse' : 'row'}
              justifyContent='space-between'
              gap={1}
              m={isMobile ? 0 : 2}
              p={isMobile ? 1 : 2}
              alignItems='stretch'
              sx={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                width: '100%',
                height: 'fit-content',
                ...(isMobile
                  ? {
                      bgcolor: 'background.paper',
                      borderTop: 1,
                      borderColor: 'divider',
                      pb: SAFE_AREA_BOTTOM,
                      '& .MuiButton-root': { minHeight: TOUCH_MIN, width: '100%' },
                    }
                  : {}),
              }}
            >
              {readOnly ? (
                <Button type='button' color='primary' variant='contained' fullWidth onClick={onClose}>
                  Close
                </Button>
              ) : (
                <>
                  <Button type='button' color='secondary' fullWidth onClick={handleCancel} disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button type='submit' variant='contained' fullWidth disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting…' : 'Submit'}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Field Reset Confirmation Dialog - moved outside main Dialog */}
      {pendingFieldChange && (
        <FieldResetConfirmationDialog
          open={resetConfirmationOpen}
          onClose={handleConfirmationClose}
          onConfirm={handleConfirmationConfirm}
          parentField={pendingFieldChange.parentField}
          parentValue={pendingFieldChange.parentValue}
          dependentField={pendingFieldChange.dependentField}
          dependentValue={pendingFieldChange.dependentValue}
        />
      )}

      {/* Save Location Dialog */}
      {locationToSave && (
        <AddLocationDialog
          open={saveLocationDialogOpen}
          onClose={() => {
            setSaveLocationDialogOpen(false)
            setLocationToSave(null)
          }}
          location={locationToSave}
          isRepairShop={false}
          onCreate={handleSaveLocation}
          onError={(message) => {
            showError(message)
          }}
        />
      )}

      <AuctionDialog
        open={auctionDialogOpen}
        onClose={() => setAuctionDialogOpen(false)}
        onCreate={handleCreateAuction}
        onUpdate={async () => undefined}
        onError={(message) => {
          showError(message)
        }}
      />

      <ConfirmDialog
        open={Boolean(gatePassPendingDelete)}
        title="Remove gate pass?"
        message={
          gatePassPendingDelete
            ? `Remove "${gatePassPendingDelete.fileName}" from this vehicle? Transport will no longer see this file. You can upload a replacement afterward.`
            : ''
        }
        confirmText={isDeletingGatePass ? 'Removing…' : 'Remove'}
        cancelText="Cancel"
        severity="error"
        onCancel={() => {
          if (!isDeletingGatePass) setGatePassPendingDelete(null)
        }}
        onConfirm={() => {
          if (gatePassPendingDelete && !isDeletingGatePass) {
            void handleGatePassDelete(gatePassPendingDelete)
          }
        }}
      />

      {/* Purchased without pickup address — notify stock number owner */}
      <Dialog
        open={purchaseNotifyDialogOpen}
        onClose={() => setPurchaseNotifyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pickup Location Needed</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Trucks without pickup addresses cannot be purchased for inventory. Would you like to notify the stock number owner?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseNotifyDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              const sent = await handleNotifyPickupLocationNeeded()
              if (sent) {
                setPurchaseNotifyDialogOpen(false)
              }
            }}
            color="primary"
            variant="contained"
            disabled={!vehicle?.id || vehicle?.ready_for_transport?.needs_address === true || isSendingPickupNotify}
          >
            {vehicle?.ready_for_transport?.needs_address === true
              ? 'Notification already sent'
              : isSendingPickupNotify
                ? 'Notifying…'
                : 'Notify'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Sale Information Dialog */}
      {soldDialogOpen && vehicle && shouldShowEditSaleButton() && (
        <Suspense fallback={null}>
          <LazySoldDialog
            open={soldDialogOpen}
            onClose={() => setSoldDialogOpen(false)}
            onSubmit={handleSoldDialogSubmit}
            itemName={vehicle.stock_number}
            actionType={getActionTypeFromStatus(vehicle.status) || 'sold'}
            price={vehicle.optic_list_price ?? undefined}
            currentLocation={vehicle.location ?? undefined}
            initialData={buildInitialData()}
          />
        </Suspense>
      )}
    </>
  )
}

export default VehicleForm
